import { state, api, $, $$, toast, escHtml, truncate, todayStr, localDateKey, _timeAgo, _ensureMachineCache, _allMachinesCache, _lineMap, _populateLineSelect, _linkLineToMachineSelect, _insertLineSelectBefore, customConfirm, formatDateRO, formatDateTimeRO } from './core';
import { colleagueMd } from './colleague';
import { mountWfPdfViewer, type WfPdfViewerHandle } from './pdf-viewer';
import { incarcaSugestiiInterval } from './procedures';
import { askText } from './dialogs';
// Alocarea din raportul de compliance refolosește dialogul din calendar —
// aceeași lucrare, același flux (audit UX, 8 aug 2026). Fără import circular:
// tasks-calendar nu importă nimic din reports.
import { _openAssignDialog } from './tasks-calendar';
import type { FactoryStructure, JurnalDetaliu } from './types';
import type { RandDiagnosticCases, RandMaintenanceIntervals, RandMaintenanceLog, RandMaintenanceWorkflowEnrichments,
    RandMaintenanceWorkflows, RandModels3d, RandMonthlyReports, RandWorkflowMedia } from './tipuri-baza';

/* ── Ce trimite serverul (routes/maintenance/rapoarte.py, workflow.py, intervale.py) ── */
/** o notă din jurnalul unei lucrări (workflow.py, ruta …/notes). Notele migrate din textul vechi au doar `text` și
 *  `at: 'migrated'` — fără categorie (cade pe «altele») */
interface NotaOperator {
    text?: string; category?: string; step?: number | null; at?: string; by?: string;
    photo_id?: number | null; issue_id?: number | null;
}
/** /api/reports/monthly/archive: rândul din monthly_reports + dacă PDF-ul mai e pe disc */
interface ArhivaLunara extends RandMonthlyReports { exists: boolean; }
/** /api/reports/monthly/stats */
interface StatisticiLunare {
    year: number | null; month: number | null; period_label: string; scope_type: string; scope_id: string | null;
    scope_label: string | null; scope_machines: string[] | null; period: { start: string; end: string };
    logs: number; total_minutes: number; technicians: number; workflows_by_status: Record<string, number>;
    issues_reported: number;
}
/** /api/maintenance_workflows: rândul + numele utilajului și acțiunea intervalului; cu `limit` vine pe pagini */
interface FluxRaport extends RandMaintenanceWorkflows { machine_name: string | null; action_ro: string | null; }
type ListaFluxuri = FluxRaport[] | { items: FluxRaport[]; total: number; limit: number; offset: number };
/** /api/maintenance_workflows/<id>: același rând + fișierele atașate */
interface FluxDetaliu extends FluxRaport { media: RandWorkflowMedia[]; }
/** /api/work/workflow/<id>/enrichments (imbogatiri.py): completarea + `file_url` când are fișier */
interface CompletareFlux extends RandMaintenanceWorkflowEnrichments { file_url?: string; }
/** un pas din `steps_data` (JSON scris de ecranul de execuție, workflow.ts) */
interface PasFlux {
    step?: number; text?: string; done?: boolean; completed_by?: string; completed_at?: string;
    duration_seconds?: number | null; notes?: string;
}
/** un pas din sugestiile AI ale unei lecții, cum le desparte `_parseAiSteps` */
interface PasAi { text: string; removed: boolean; isHeader?: boolean; }
/** un pas al intervenției ghidate de AI, salvat în `notes` (journal.ts) */
interface PasSalvat { step: number; text: string; completed: boolean; feedback: string; deleted: boolean; }
/** /api/reports/saptamana: rândul pe linie și pe om */
interface LinieSaptamana {
    linie: string; facut: number; minute: number; restant: number | null; restant_nou: number | null;
    restant_critic: number | null; de_validat: number;
}
interface OmSaptamana { om: string; facut: number; minute: number; de_validat: number; amanari: number; linii: string[]; }
interface RaportSaptamana {
    period: { start: string; end: string; saptamana: number; eticheta: string };
    scope: { type: string; id: string | null; label: string };
    restante_numarate: { zile: string[]; prima: string | null; ultima: string | null };
    definitii: Record<string, string>;
    total: { facut: number; minute: number; de_validat: number; restant: number | null; restant_nou: number | null };
    pe_linie: LinieSaptamana[];
    pe_om: OmSaptamana[];
}
/** /api/reports/compliance/by-user: o lucrare a omului — făcută (din jurnal) sau amânată */
interface LucrareOm {
    machine_id: string; machine_name: string; line: string; action: string; planned_date: string; done_date: string;
    status: string; duration_min: number; validation_status: string; log_id: number | null; sort_date: string;
    postponed_until?: string; reason_code?: string; reason_text?: string;
}
interface OmConformitate {
    user: string; role: string;
    stats: { done: number; postponed: number; missed: number; minutes: number; cu_durata: number; planned: number; compliance_pct: number };
    tasks: LucrareOm[];
}
interface ConformitatePeOm {
    period: { start: string; end: string };
    users_available: string[];
    summary: {
        users_count: number; total_done: number; total_postponed: number; total_missed: number; total_minutes: number;
        total_cu_durata: number; total_planned: number; compliance_pct: number;
    };
    per_user: OmConformitate[];
}
/** /api/reports/maintenance: rândul din jurnal + utilaj, linie efectivă, intervalul și pagina din manual */
interface IntrareJurnalRaport extends RandMaintenanceLog {
    machine_name: string | null; line_efectiv: string | null; interval_manual: string | null;
    interval_category: string | null; interval_critical: number | null; manual_document_id: number | null;
    manual_page: number | null; manual_filename: string | null; tip: string; tip_eticheta: string;
}
interface RaportJurnal {
    entries: IntrareJurnalRaport[]; by_machine: Record<string, number>; by_category: Record<string, number>;
    by_tip: Record<string, number>; tipuri: Record<string, string>; total: number; synthetic_count: number;
    limit: number; offset: number;
}
/** /api/reports/compliance: o lucrare recurentă cu cât s-a făcut din cât trebuia (`_calcul_conformitate`) */
interface RandConformitate {
    machine_id: string; machine_name: string; line: string; interval_id: number; action_ro: string;
    interval_text: string | null; interval_hours: number | null; cal_days: number | null; cadenta_fel: string;
    ore_functionare_perioada: number | null; critical: number | null; category: string | null;
    expected: number; completed: number; compliance_pct: number; severity: string; recommendation: string;
    manual_document_id: number | null; manual_page: number | null;
    pause_days_excluded?: number;   // zilele în care linia a stat (opriri peste o oră), scoase din cerut
    ore_oprita?: number;
}
interface RaportConformitate {
    period: { start: string; end: string }; fel: string; effective_end: string;
    summary: { total: number; critical: number; warning: number; ok: number; avg_compliance: number; pe_fel: { calendar: number; ore: number } };
    items: RandConformitate[];
}
/** /api/intervals/event-triggered (intervale.py): lucrările la eveniment, grupate pe felul evenimentului */
interface GrupaEveniment {
    tip: string; eticheta: string; auto: boolean;
    items: Pick<RandMaintenanceIntervals, 'id' | 'machine_id' | 'interval_text' | 'action_ro' | 'category' | 'critical'
        | 'document_id' | 'page_number'>[];
}

/* ═══ RAPOARTE MENTENANȚĂ ═══════════════════════════════════════ */
export let reportsInited = false;

/* ── Taburi (3 aug 2026) ────────────────────────────────────────
   Cele 4 rapoarte se încărcau toate la intrarea în secțiune (3 cereri
   grele, pagină aglomerată). Acum se încarcă doar tabul deschis, o
   singură dată; „Filtrează/Calculează" reîncarcă explicit. */
const _RPT_LOADERS: Record<string, () => unknown> = {   // rezultatul trece doar prin Promise.resolve
    'reports-tab-workflows':  () => _loadWorkflowReports(),
    'reports-tab-users':      () => loadUserComplianceReport(),
    'reports-tab-compliance': () => loadComplianceReport(),
    'reports-tab-journal':    () => loadReportsData(),
    'reports-tab-monthly':    () => loadMonthlyReport(),
    'reports-tab-briefing':   () => loadBriefingProposals(),
    'reports-tab-evenimente': () => loadEventIntervals(),
    'reports-tab-saptamana':  () => loadRaportSaptamana(),
};
let _rptActiveTab = 'reports-tab-workflows';
const _rptLoaded = new Set<string>();

function _rptShowTab(tabId: string, force = true) {
    if (!_RPT_LOADERS[tabId]) return;
    // Reîncărcare la FIECARE deschidere de tab (3 aug 2026): cu cache pe tab,
    // dacă validai o intervenție și te întorceai în Rapoarte, tabul deja
    // vizitat arăta datele vechi — părea că lucrarea „a dispărut".
    if (force) _rptLoaded.delete(tabId);
    _rptActiveTab = tabId;
    document.querySelectorAll('#reports-tabs .rpt-tab').forEach(b => {
        const on = (b as HTMLElement).dataset.tab === tabId;
        b.classList.toggle('active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('#sec-reports .rpt-panel').forEach(p => {
        p.classList.toggle('active', p.id === tabId);
    });
    if (!_rptLoaded.has(tabId)) {
        _rptLoaded.add(tabId);
        Promise.resolve(_RPT_LOADERS[tabId]()).catch(() => _rptLoaded.delete(tabId));
    }
}

const _REPORT_CAT_META: Record<string, { icon: string; color: string; label: string }> = {
    observatie: { icon: '👁', color: '#60a5fa', label: 'Observație' },
    problema:   { icon: '⚠', color: '#ef4444', label: 'Problemă' },
    masurare:   { icon: '📏', color: '#a78bfa', label: 'Măsurare' },
    predare_schimb: { icon: '🔄', color: '#f59e0b', label: 'Predare schimb' },
    altele:     { icon: '📋', color: '#94a3b8', label: 'Altele' },
};

function _renderStructuredNotes(rawNotes: string): string {
    let notesList: NotaOperator[] = [];
    try {
        if (rawNotes.startsWith('[')) {
            notesList = JSON.parse(rawNotes);
        } else {
            return '<div style="margin-top:12px;padding:10px;background:rgba(255,255,255,.04);border-radius:6px"><strong>Note operator:</strong> ' + escHtml(rawNotes) + '</div>';
        }
    } catch {
        return '<div style="margin-top:12px;padding:10px;background:rgba(255,255,255,.04);border-radius:6px"><strong>Note operator:</strong> ' + escHtml(rawNotes) + '</div>';
    }
    if (notesList.length === 0) return '';

    let html = '<div style="margin-top:12px"><strong style="font-size:.9rem">📋 Jurnal note (' + notesList.length + ')</strong>';
    html += '<div style="margin-top:6px;display:flex;flex-direction:column;gap:4px">';
    notesList.forEach((n) => {
        // nota migrată n-are categorie: cheia «undefined» nu există și cade pe «altele»
        const meta = _REPORT_CAT_META[n.category as string] || _REPORT_CAT_META.altele;
        const time = n.at ? new Date(n.at).toLocaleString('ro-RO', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '';
        html += '<div style="padding:6px 10px;border-radius:6px;background:rgba(255,255,255,.04);border-left:3px solid ' + meta.color + '">';
        html += '<span style="font-size:.72rem;font-weight:600;padding:1px 6px;border-radius:6px;background:' + meta.color + '22;color:' + meta.color + '">' + meta.icon + ' ' + meta.label + '</span>';
        if (n.step) html += ' <span style="font-size:.72rem;color:var(--text-muted)">Pas ' + n.step + '</span>';
        if (time) html += ' <span style="font-size:.72rem;color:var(--text-muted);float:right">' + escHtml(time) + '</span>';
        html += '<div style="font-size:.85rem;margin-top:2px">' + escHtml(n.text || '') + '</div>';
        const footParts: string[] = [];
        if (n.by) footParts.push(escHtml(n.by));
        if (n.photo_id) footParts.push('📷 foto');
        if (n.issue_id) footParts.push('<span style="color:#ef4444">Problemă #' + n.issue_id + '</span>');
        if (footParts.length) html += '<div style="font-size:.72rem;color:var(--text-muted);margin-top:1px">' + footParts.join(' · ') + '</div>';
        html += '</div>';
    });
    html += '</div></div>';
    return html;
}

export async function loadReports() {
    const machineSel = $('#reports-machine');
    const categorySel = $('#reports-category');
    const fromInput = $('#reports-from');
    const toInput = $('#reports-to');

    if (!reportsInited) {
        await _ensureMachineCache();
        const [structure, intervals]: [FactoryStructure | null, RandMaintenanceIntervals[]] = await Promise.all([
            api('/api/factory_structure').catch(() => null),
            api('/api/maintenance_intervals').catch(() => []),
        ]);
        if (machineSel && structure && structure.production_lines) {
            machineSel.innerHTML = '<option value="">Toate</option>';
            const machineIds = new Set<string>();
            structure.production_lines.forEach((pl) => {
                machineIds.add(pl.id);
                (pl.equipment || []).forEach((eq: { id: string; }) => { machineIds.add(eq.id); });
            });
            [...machineIds].sort().forEach(mid => { machineSel.innerHTML += `<option value="${escHtml(mid)}">${escHtml(mid)}</option>`; });
            _insertLineSelectBefore(machineSel, 'reports-line', 'Toate liniile', 'Toate');
        }
        // 30 sep 2026: lista de categorii venea din PLAN (intervale: „curatare", „inspectii"…),
        // iar filtrul caută în JURNAL, unde categoria e scrisă normalizat („curățare") — patru
        // alegeri dădeau mereu 0 rânduri, iar „urgent" (155 de lucrări) lipsea din listă.
        // Acum: categoriile care chiar există în jurnal.
        if (categorySel) {
            let cats: string[] = [];
            try {
                const r = await api('/api/reports/maintenance?limit=1&include_synthetic=0');
                cats = Object.keys((r && r.by_category) || {}).filter(c => c && c !== '_fără_categorie').sort();
            } catch (_e) {
                cats = [...new Set((intervals || []).map((i) => i.category).filter(Boolean))].sort() as string[];
            }
            categorySel.innerHTML = '<option value="">Toate</option>';
            cats.forEach(c => { categorySel.innerHTML += `<option value="${escHtml(c)}">${escHtml(c)}</option>`; });
        }
        const wfMachineSel = document.getElementById('wf-reports-machine');
        if (wfMachineSel && structure && structure.production_lines) {
            wfMachineSel.innerHTML = '<option value="">Toate</option>';
            const wfMachineIds = new Set<string>();
            structure.production_lines.forEach((pl) => {
                wfMachineIds.add(pl.id);
                (pl.equipment || []).forEach((eq: { id: string; }) => { wfMachineIds.add(eq.id); });
            });
            [...wfMachineIds].sort().forEach(mid => { wfMachineSel.innerHTML += `<option value="${escHtml(mid)}">${escHtml(mid)}</option>`; });
            _insertLineSelectBefore(wfMachineSel, 'wf-reports-line', 'Toate liniile', 'Toate');
        }
        // Perioadă implicită: ultimele 30 de zile. Înainte se cerea tot istoricul
        // la fiecare deschidere (1614 rânduri și în creștere).
        _applyJournalPreset('last_30');
        document.querySelectorAll('.rpt-journal-preset').forEach(btn => {
            btn.addEventListener('click', () => {
                _applyJournalPreset((btn as HTMLElement).dataset.preset || 'last_30');
                loadReportsData(1);
            });
        });
        document.getElementById('reports-export-csv')?.addEventListener('click', () => _exportJournalCsv());
        document.getElementById('wf-reports-export-csv')?.addEventListener('click', () => _exportWorkflowsCsv());
        $('#reports-apply')?.addEventListener('click', () => loadReportsData(1));
        document.getElementById('wf-reports-apply')?.addEventListener('click', () => _loadWorkflowReports(1));

        /* 5 sep 2026 — Dan: «tot nu se aplică filtrul». Se aplica, dar DOAR după
           apăsarea butonului «Filtrează», în timp ce butoanele de perioadă
           («Ultimele 30 zile») reîncărcau singure. Alegeai linia, tabelul rămânea
           cum era, și concluzia firească e că filtrul e stricat. Acum orice
           alegere din filtre reîncarcă imediat; butonul rămâne, pentru cine îl
           caută. La schimbarea liniei, lista de utilaje se rescrie tot pe
           `change` (core.ts) — de aceea reîncărcăm după ea, nu înaintea ei. */
        /* 30 sep 2026: ascultătorii se legau pe id, iar selectele de linie din „Compliance" și
           „Execuție per tehnician" se creează abia MAI JOS (și în _initUserComplianceFilters)
           — `getElementById` le dădea null, deci alegerea liniei acolo nu reîncărca nimic, exact
           plângerea din 5 sep. Acum o singură ascultare pe secțiune, pe id-ul țintei: prinde
           și selectele create mai târziu. */
        const REINCARCA: Record<string, () => void> = {
            'reports-line': () => loadReportsData(1), 'reports-machine': () => loadReportsData(1),
            'reports-category': () => loadReportsData(1), 'reports-tip': () => loadReportsData(1),
            'wf-reports-line': () => _loadWorkflowReports(1), 'wf-reports-machine': () => _loadWorkflowReports(1),
            'wf-reports-status': () => _loadWorkflowReports(1),
            'compliance-line': () => loadComplianceReport(), 'compliance-machine': () => loadComplianceReport(),
            'compliance-fel': () => loadComplianceReport(),
            'user-compliance-line': () => loadUserComplianceReport(),
            'user-compliance-machine': () => loadUserComplianceReport(),
            'user-compliance-user': () => loadUserComplianceReport(),
        };
        document.getElementById('sec-reports')?.addEventListener('change', ev => {
            const f = REINCARCA[(ev.target as HTMLElement | null)?.id || ''];
            if (f) setTimeout(f, 0);
        });
        document.getElementById('reports-reset')?.addEventListener('click', () => {
            const rl = document.getElementById('reports-line');
            if (rl) { (rl as HTMLInputElement).value = ''; rl.dispatchEvent(new Event('change')); }
            if ($('#reports-machine')) ($('#reports-machine') as HTMLInputElement).value = '';
            if ($('#reports-category')) ($('#reports-category') as HTMLInputElement).value = '';
            const tipSel = document.getElementById('reports-tip') as HTMLSelectElement | null;
            if (tipSel) tipSel.value = '';
            const synthCb = document.getElementById('reports-show-synthetic') as HTMLInputElement | null;
            if (synthCb) synthCb.checked = false;
            _applyJournalPreset('last_30');
            loadReportsData(1);
        });
        document.getElementById('wf-reports-reset')?.addEventListener('click', () => {
            const wrl = document.getElementById('wf-reports-line');
            if (wrl) { (wrl as HTMLInputElement).value = ''; wrl.dispatchEvent(new Event('change')); }
            if (document.getElementById('wf-reports-machine')) (document.getElementById('wf-reports-machine') as HTMLInputElement).value = '';
            if (document.getElementById('wf-reports-status')) (document.getElementById('wf-reports-status') as HTMLInputElement).value = '';
            const cancelCb = document.getElementById('wf-reports-show-cancelled') as HTMLInputElement | null;
            if (cancelCb) cancelCb.checked = false;
            _loadWorkflowReports(1);
        });

        const complianceMachineSel = document.getElementById('compliance-machine') as HTMLSelectElement | null;
        if (complianceMachineSel && structure && structure.production_lines) {
            complianceMachineSel.innerHTML = '<option value="">Toate</option>';
            const cMachineIds = new Set<string>();
            structure.production_lines.forEach((pl) => {
                cMachineIds.add(pl.id);
                (pl.equipment || []).forEach((eq) => { cMachineIds.add(eq.id); });
            });
            [...cMachineIds].sort().forEach(mid => { complianceMachineSel.innerHTML += `<option value="${escHtml(mid)}">${escHtml(mid)}</option>`; });
            _insertLineSelectBefore(complianceMachineSel, 'compliance-line', 'Toate liniile', 'Toate');
        }
        const today = new Date();
        const fromInput2 = document.getElementById('compliance-from') as HTMLInputElement | null;
        const toInput2 = document.getElementById('compliance-to') as HTMLInputElement | null;
        if (fromInput2 && !fromInput2.value) {
            const firstDayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            fromInput2.value = localDateKey(firstDayMonth);
        }
        if (toInput2 && !toInput2.value) {
            toInput2.value = localDateKey(today);
        }
        document.getElementById('compliance-apply')?.addEventListener('click', () => loadComplianceReport());
        document.getElementById('compliance-reset')?.addEventListener('click', () => {
            const cl = document.getElementById('compliance-line') as HTMLSelectElement | null;
            if (cl) { cl.value = ''; cl.dispatchEvent(new Event('change')); }
            if (complianceMachineSel) complianceMachineSel.value = '';
            const felSel = document.getElementById('compliance-fel') as HTMLSelectElement | null;
            if (felSel) felSel.value = 'toate';
            if (fromInput2) {
                const fd = new Date(today.getFullYear(), today.getMonth(), 1);
                fromInput2.value = localDateKey(fd);
            }
            if (toInput2) toInput2.value = localDateKey(today);
            loadComplianceReport();
        });
        document.getElementById('compliance-export-csv')?.addEventListener('click', () => exportComplianceCsv());

        _initUserComplianceFilters(structure);
        _initMonthlyFilters(structure);

        document.querySelectorAll('#reports-tabs .rpt-tab').forEach(btn => {
            btn.addEventListener('click', () => _rptShowTab((btn as HTMLElement).dataset.tab || ''));
        });
        document.getElementById('wf-reports-show-cancelled')?.addEventListener('change', () => _loadWorkflowReports(1));
        document.getElementById('reports-show-synthetic')?.addEventListener('change', () => loadReportsData(1));

        reportsInited = true;
    }

    // La fiecare intrare în secțiune împrospătăm tabul deschis; celelalte rămân
    // leneșe (se încarcă la primul click) și cache-uite până se schimbă tabul.
    _rptShowTab(_rptActiveTab, true);
}

/* ── Raport lunar PDF (3 aug 2026) ──────────────────────────────
   Endpoint-urile /api/reports/monthly (PDF) și /monthly/stats existau de la
   FAZA 8.7, dar nu erau apelate de nicăieri din interfață. */

const _LUNI_RO = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
                  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];

/* Perioada raportului (5 sep 2026, cerere Dan: «adaugă selecție perioadă»).
   Un audit ISO cere de obicei un trimestru sau un an, nu o lună. Serverul
   acceptă `year+month` SAU `start+end`; aici alegem care, după ce a bifat omul,
   și întoarcem și numele perioadei, pentru titlul ferestrei de vizualizare. */
function _monthlyPeriodParams(): { q: string; eticheta: string } {
    const fel = (document.getElementById('monthly-period-kind') as HTMLSelectElement | null)?.value || 'luna';
    const an = (document.getElementById('monthly-year') as HTMLSelectElement | null)?.value
        || String(new Date().getFullYear());
    const luna = (document.getElementById('monthly-month') as HTMLSelectElement | null)?.value || '1';
    const trim = (document.getElementById('monthly-quarter') as HTMLSelectElement | null)?.value || '1';
    const ultimaZi = (y: number, m: number) => new Date(y, m, 0).getDate();
    const zi2 = (n: number) => String(n).padStart(2, '0');

    if (fel === 'luna') {
        return { q: `year=${an}&month=${luna}`, eticheta: `${_LUNI_RO[+luna - 1]} ${an}` };
    }
    if (fel === 'trimestru') {
        const primaLuna = (+trim - 1) * 3 + 1;
        const ultLuna = primaLuna + 2;
        return {
            q: `start=${an}-${zi2(primaLuna)}-01&end=${an}-${zi2(ultLuna)}-${zi2(ultimaZi(+an, ultLuna))}`,
            eticheta: `Trimestrul ${['I', 'II', 'III', 'IV'][+trim - 1]} ${an}`,
        };
    }
    if (fel === 'an') {
        return { q: `start=${an}-01-01&end=${an}-12-31`, eticheta: `Anul ${an}` };
    }
    const de_la = (document.getElementById('monthly-from') as HTMLInputElement | null)?.value || '';
    const pana_la = (document.getElementById('monthly-to') as HTMLInputElement | null)?.value || '';
    return { q: `start=${de_la}&end=${pana_la}`, eticheta: `${de_la} – ${pana_la}` };
}

/** Un interval ales pe jumătate (fără capăt) ar cere serverului un raport gol. */
function _perioadaValida(per: { q: string; eticheta: string }): boolean {
    if (/start=&|end=$|start=$|end=&/.test(per.q + '&')) {
        toast('Alege și data de început, și data de sfârșit.', 'error');
        return false;
    }
    const m = per.q.match(/start=([\d-]+)&end=([\d-]+)/);
    if (m && m[2] < m[1]) {
        toast('Sfârșitul perioadei e înaintea începutului.', 'error');
        return false;
    }
    return true;
}

function _monthlyScopeParams(): string {
    const line = (document.getElementById('monthly-line') as HTMLSelectElement | null)?.value || '';
    const machine = (document.getElementById('monthly-machine') as HTMLSelectElement | null)?.value || '';
    // Utilajul e mai specific decât linia — dacă e ales, primează (la fel pe server).
    if (machine) return `&machine_id=${encodeURIComponent(machine)}`;
    if (line) return `&line_id=${encodeURIComponent(line)}`;
    return '';
}

function _initMonthlyFilters(structure: FactoryStructure | null) {
    const monthSel = document.getElementById('monthly-month') as HTMLSelectElement | null;
    const yearSel = document.getElementById('monthly-year') as HTMLSelectElement | null;
    if (!monthSel || !yearSel) return;
    const now = new Date();
    if (!monthSel.options.length) {
        monthSel.innerHTML = _LUNI_RO.map((l, i) => `<option value="${i + 1}">${l}</option>`).join('');
        monthSel.value = String(now.getMonth() + 1);
    }
    if (!yearSel.options.length) {
        const y = now.getFullYear();
        yearSel.innerHTML = [y, y - 1, y - 2].map(v => `<option value="${v}">${v}</option>`).join('');
        yearSel.value = String(y);
    }
    // Selectorul de utilaj + cel de linie (inserat automat în stânga lui, ca în
    // restul aplicației: alegi linia → lista de utilaje se restrânge).
    const machineSel = document.getElementById('monthly-machine') as HTMLSelectElement | null;
    if (machineSel && structure && structure.production_lines) {
        const ids = new Set<string>();
        structure.production_lines.forEach((pl) => {
            ids.add(pl.id);
            (pl.equipment || []).forEach((eq) => ids.add(eq.id));
        });
        machineSel.innerHTML = '<option value="">Toate</option>'
            + [...ids].sort().map(mid => `<option value="${escHtml(mid)}">${escHtml(mid)}</option>`).join('');
        _insertLineSelectBefore(machineSel, 'monthly-line', 'Toate liniile', 'Toate');
        document.getElementById('monthly-line')?.addEventListener('change', () => loadMonthlyReport());
        machineSel.addEventListener('change', () => loadMonthlyReport());
    }
    /* Câmpurile care se văd depind de felul perioadei: la trimestru nu are ce
       căuta luna, la interval nu are ce căuta nici anul. */
    const felSel = document.getElementById('monthly-period-kind') as HTMLSelectElement | null;
    const aratа = (id: string, da: boolean) => {
        const el = document.getElementById(id);
        if (el) (el as HTMLElement).style.display = da ? '' : 'none';
    };
    const potriveste = () => {
        const fel = felSel?.value || 'luna';
        aratа('monthly-month-wrap', fel === 'luna');
        aratа('monthly-quarter-wrap', fel === 'trimestru');
        aratа('monthly-year-wrap', fel !== 'interval');
        aratа('monthly-from-wrap', fel === 'interval');
        aratа('monthly-to-wrap', fel === 'interval');
    };
    potriveste();
    // capete implicite pentru intervalul liber: ultimele 90 de zile
    const dela = document.getElementById('monthly-from') as HTMLInputElement | null;
    const panala = document.getElementById('monthly-to') as HTMLInputElement | null;
    if (dela && !dela.value) {
        const d = new Date(); d.setDate(d.getDate() - 90);
        dela.value = localDateKey(d);
    }
    if (panala && !panala.value) panala.value = localDateKey(new Date());

    felSel?.addEventListener('change', () => { potriveste(); loadMonthlyReport(); });
    document.getElementById('monthly-quarter')?.addEventListener('change', () => loadMonthlyReport());
    dela?.addEventListener('change', () => loadMonthlyReport());
    panala?.addEventListener('change', () => loadMonthlyReport());
    monthSel.addEventListener('change', () => loadMonthlyReport());
    yearSel.addEventListener('change', () => loadMonthlyReport());
    document.getElementById('monthly-refresh')?.addEventListener('click', () => loadMonthlyReport());
    document.getElementById('monthly-archive-scope')?.addEventListener('change', () => _loadMonthlyArchive());
    document.getElementById('monthly-view')?.addEventListener('click', () => {
        const per = _monthlyPeriodParams();
        if (!_perioadaValida(per)) return;
        _openReportViewer(`/api/reports/monthly?${per.q}${_monthlyScopeParams()}&inline=1`,
                          `/api/reports/monthly?${per.q}${_monthlyScopeParams()}`,
                          `Raport ${per.eticheta}`);
        // Generarea îl și arhivează pe server — reîmprospătăm lista.
        setTimeout(() => _loadMonthlyArchive(), 2500);
    });
    document.getElementById('monthly-download')?.addEventListener('click', () => {
        const per = _monthlyPeriodParams();
        if (!_perioadaValida(per)) return;
        toast(`Se generează raportul pe ${per.eticheta}…`);
        window.location.href = `/api/reports/monthly?${per.q}${_monthlyScopeParams()}`;
        setTimeout(() => _loadMonthlyArchive(), 2500);
    });
    document.getElementById('monthly-viewer-close')?.addEventListener('click', () => _closeReportViewer());
    document.getElementById('monthly-viewer-prev')?.addEventListener('click', () => {
        if (_reportViewer) _reportViewer.goToPage(Math.max(1, _viewerPage - 1));
    });
    document.getElementById('monthly-viewer-next')?.addEventListener('click', () => {
        if (_reportViewer) _reportViewer.goToPage(_viewerPage + 1);
    });
}

/* Vizualizare în pagină (cerere Dan, 3 aug 2026): raportul se citește pe loc,
   descărcarea e o acțiune separată. Folosim același vizualizator pdf.js ca la
   manuale — merge și pe tabletă, unde iframe-ul cu PDF ar forța descărcarea. */
let _reportViewer: WfPdfViewerHandle | null = null;
let _viewerPage = 1;

function _closeReportViewer() {
    if (_reportViewer) { try { _reportViewer.destroy(); } catch { /* deja distrus */ } }
    _reportViewer = null;
    const wrap = document.getElementById('monthly-viewer-wrap');
    if (wrap) wrap.hidden = true;
}

function _openReportViewer(inlineUrl: string, downloadUrl: string, titlu: string) {
    const wrap = document.getElementById('monthly-viewer-wrap');
    const host = document.getElementById('monthly-viewer');
    if (!wrap || !host) return;
    _closeReportViewer();
    wrap.hidden = false;
    const titleEl = document.getElementById('monthly-viewer-title');
    if (titleEl) titleEl.textContent = titlu;
    const dl = document.getElementById('monthly-viewer-download') as HTMLAnchorElement | null;
    if (dl) dl.href = downloadUrl;
    const pagesEl = document.getElementById('monthly-viewer-pages');
    if (pagesEl) pagesEl.textContent = '';
    _viewerPage = 1;
    _reportViewer = mountWfPdfViewer({
        container: host,
        docId: 0,
        page: 1,
        fileUrl: inlineUrl,
        loadingLabel: 'raportul',
        onPageChange: (page: number, total: number) => {
            _viewerPage = page;
            if (pagesEl) pagesEl.textContent = `pagina ${page} din ${total}`;
            const prev = document.getElementById('monthly-viewer-prev') as HTMLButtonElement | null;
            const next = document.getElementById('monthly-viewer-next') as HTMLButtonElement | null;
            if (prev) prev.disabled = page <= 1;
            if (next) next.disabled = page >= total;
        },
    });
    wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function _loadMonthlyArchive() {
    const tbody = document.querySelector('#monthly-archive-table tbody') as HTMLElement | null;
    const countEl = document.getElementById('monthly-archive-count');
    if (!tbody) return;
    const scope = (document.getElementById('monthly-archive-scope') as HTMLSelectElement | null)?.value || '';
    let d: { items: ArhivaLunara[]; total: number };
    try {
        d = await api('/api/reports/monthly/archive' + (scope ? `?scope_type=${scope}` : ''));
    } catch {
        tbody.innerHTML = '<tr><td colspan="8" class="empty-state" style="color:var(--red)">Eroare la încărcarea arhivei</td></tr>';
        return;
    }
    const items = d.items || [];
    if (countEl) countEl.textContent = items.length ? `${items.length} rapoarte salvate` : '';
    if (!items.length) {
        tbody.innerHTML = '<tr><td colspan="8" class="empty-state">Niciun raport generat încă. Alege luna (și, dacă vrei, linia sau utilajul) și apasă «Generează și descarcă PDF».</td></tr>';
        return;
    }
    const scopeIco: Record<string, string> = { fabrica: '🏭', linie: '🔗', masina: '⚙️' };
    tbody.innerHTML = items.map((r) => `
      <tr>
        <td style="white-space:nowrap"><strong>${escHtml(r.period_label || (_LUNI_RO[(r.month || 1) - 1] + ' ' + r.year))}</strong></td>
        <td>${scopeIco[r.scope_type] || '📄'} ${escHtml(r.scope_label || r.scope_id || 'Toată fabrica')}</td>
        <td style="text-align:center">${r.n_logs ?? '—'}</td>
        <td style="text-align:center;white-space:nowrap">${_formatHours(r.total_minutes || 0)}</td>
        <td style="text-align:center;white-space:nowrap">${r.avg_compliance != null
            ? `<strong style="color:${r.avg_compliance >= 80 ? 'var(--green,#22c55e)' : (r.avg_compliance >= 50 ? 'var(--orange,#f59e0b)' : 'var(--red,#ef4444)')}">${r.avg_compliance}%</strong>${
                r.n_critical_gaps ? `<br><span class="muted" style="font-size:.7rem">${r.n_critical_gaps} critice</span>` : ''}`
            : '<span class="muted">—</span>'}</td>
        <td style="white-space:nowrap;font-size:.85rem">${escHtml(r.generated_at || '')}</td>
        <td style="font-size:.85rem">${escHtml(r.generated_by || '—')}</td>
        <td style="white-space:nowrap">${r.exists
            ? `<button type="button" class="btn btn-sm rpt-arch-view" data-id="${r.id}"
                       data-titlu="${escHtml(_LUNI_RO[(r.month || 1) - 1] + ' ' + r.year + ' — ' + (r.scope_label || ''))}">👁 Vezi</button>
               <a class="btn btn-sm btn-outline" href="/api/reports/monthly/archive/${r.id}" style="margin-left:4px">⬇</a>
               <span class="muted" style="font-size:.72rem;margin-left:6px">${Math.round((r.size_bytes || 0) / 1024)} KB</span>`
            : '<span class="muted" style="font-size:.8rem">fișier lipsă — regenerează</span>'}</td>
      </tr>`).join('');

    tbody.querySelectorAll('.rpt-arch-view').forEach(btn => {
        btn.addEventListener('click', () => {
            const id = (btn as HTMLElement).dataset.id;
            _openReportViewer(`/api/reports/monthly/archive/${id}?inline=1`,
                              `/api/reports/monthly/archive/${id}`,
                              (btn as HTMLElement).dataset.titlu || 'Raport lunar');
        });
    });
}

export async function loadMonthlyReport() {
    const monthSel = document.getElementById('monthly-month') as HTMLSelectElement | null;
    const yearSel = document.getElementById('monthly-year') as HTMLSelectElement | null;
    const summaryEl = document.getElementById('monthly-summary');
    const breakdownEl = document.getElementById('monthly-wf-breakdown');
    if (!summaryEl || !breakdownEl) return;
    const month = monthSel?.value || String(new Date().getMonth() + 1);
    const year = yearSel?.value || String(new Date().getFullYear());
    const per = _monthlyPeriodParams();          // lună / trimestru / an / interval

    summaryEl.innerHTML = '<span class="muted">Se calculează…</span>';
    breakdownEl.innerHTML = '';
    _loadMonthlyArchive();
    let d: StatisticiLunare;
    try {
        d = await api(`/api/reports/monthly/stats?${per.q}${_monthlyScopeParams()}`);
    } catch {
        summaryEl.innerHTML = '<span style="color:var(--red)">Eroare la încărcarea rezumatului</span>';
        return;
    }

    const scopeEl = document.getElementById('monthly-scope');
    if (scopeEl) {
        const mach = d.scope_machines;
        scopeEl.innerHTML = `<strong>${escHtml(d.scope_label || 'Toată fabrica')}</strong> · ${escHtml(d.period_label || per.eticheta)}`
            + (Array.isArray(mach) && mach.length && d.scope_type === 'linie'
                ? `<br><span class="muted" style="font-size:.82rem">Utilaje incluse: ${escHtml(mach.join(', '))}</span>` : '');
    }

    const min = d.total_minutes || 0;
    summaryEl.innerHTML = `
      <div class="stat-card" style="min-width:150px"><div class="label">Lucrări reale</div><div class="value">${d.logs || 0}</div></div>
      <div class="stat-card" style="min-width:150px"><div class="label">Timp total</div><div class="value">${_formatHours(min)}</div></div>
      <div class="stat-card" style="min-width:150px"><div class="label">Tehnicieni activi</div><div class="value">${d.technicians || 0}</div></div>
      <div class="stat-card" style="min-width:150px;border-left:3px solid #ef4444"><div class="label">Probleme raportate</div><div class="value" style="color:#ef4444">${d.issues_reported || 0}</div></div>
    `;

    const byStatus = d.workflows_by_status || {};
    const labels: Record<string, string> = {
        pending: '⏳ Pregătire', in_progress: '🔄 În desfășurare', paused: '⏸ În pauză',
        completed: '✅ Finalizate (neverificate)', validated: '✅ Validate',
        rejected: '❌ Respinse', redo_scheduled: '🔁 De refăcut', cancelled: '🚫 Anulate',
    };
    const rows = Object.entries(byStatus).filter(([, n]) => (n as number) > 0);
    if (!rows.length) {
        breakdownEl.innerHTML = `<div class="empty-state" style="padding:18px">Nicio sesiune de lucru în ${escHtml(d.period_label || per.eticheta)}.</div>`;
        return;
    }
    breakdownEl.innerHTML =
        '<h4 style="margin:6px 0 8px;font-size:.95rem">Sesiuni de lucru în lună</h4>'
        + '<div style="display:flex;gap:8px;flex-wrap:wrap">'
        + rows.sort((a, b) => (b[1] as number) - (a[1] as number)).map(([st, n]) =>
            `<span style="padding:6px 12px;border-radius:20px;background:rgba(255,255,255,.06);border:1px solid var(--border);font-size:.9rem">${escHtml(labels[st] || st)}: <strong>${n}</strong></span>`
        ).join('')
        + '</div>';
}

/* ── Propuneri din briefingul Hub-ului (3 aug 2026) ─────────────
   Jobul jobs/morning_briefing.py din bi-service scrie acțiunile operaționale
   direct în maintenance_workflows (machine_id NULL, doar text în notes).
   Nu sunt sesiuni de lucru — le arătăm separat, ca text lizibil. */
const _BRIEF_PER_PAGE = 25;
let _briefPage = 1;

export async function loadBriefingProposals(page?: number) {
    if (page && page >= 1) _briefPage = page;
    const listEl = document.getElementById('briefing-list');
    const countEl = document.getElementById('briefing-count');
    if (!listEl) return;
    listEl.innerHTML = '<div class="muted" style="padding:12px">Se încarcă…</div>';

    const params = new URLSearchParams({
        source: 'briefing', meta: '1',
        limit: String(_BRIEF_PER_PAGE),
        offset: String((_briefPage - 1) * _BRIEF_PER_PAGE),
    });
    let resp: ListaFluxuri;
    try {
        resp = await api('/api/maintenance_workflows?' + params);
    } catch {
        listEl.innerHTML = '<div class="empty-state" style="color:var(--red)">Eroare la încărcare</div>';
        return;
    }
    const items = Array.isArray(resp) ? resp : (resp?.items || []);
    const total = Array.isArray(resp) ? items.length : (resp?.total ?? items.length);
    if (countEl) {
        const first = total ? (_briefPage - 1) * _BRIEF_PER_PAGE + 1 : 0;
        countEl.textContent = items.length < total ? `${first}–${first + items.length - 1} din ${total}` : `${total} propuneri`;
    }
    if (document.getElementById('briefing-pagination')) {
        renderPagination('#briefing-pagination', total, _BRIEF_PER_PAGE, _briefPage, (p: number) => loadBriefingProposals(p));
    }
    if (!items.length) {
        listEl.innerHTML = '<div class="empty-state" style="padding:24px;text-align:center">Nicio propunere din briefing.</div>';
        return;
    }

    listEl.innerHTML = items.map((w) => {
        const raw = (w.notes || '').replace(/^\[Auto-generat din briefing #(\d+)\]\s*/, '');
        const brf = ((w.notes || '').match(/^\[Auto-generat din briefing #(\d+)\]/) || [])[1] || '';
        const [actiune, motiv] = raw.split('|').map((s: string) => (s || '').trim());
        // started_at e doar data (fără oră) — nu o trecem prin new Date(), ca să
        // nu inventăm «03:00» din decalajul UTC, cum se întâmpla în tabel.
        const zi = (w.started_at || '').slice(0, 10);
        return `
          <div style="padding:12px 14px;margin-bottom:8px;background:var(--surface);border:1px solid var(--border);border-left:3px solid #f59e0b;border-radius:10px">
            <div style="display:flex;gap:10px;align-items:baseline;flex-wrap:wrap">
              <strong style="font-size:.95rem;flex:1;min-width:200px">${escHtml(actiune || '—')}</strong>
              <span class="muted" style="font-size:.78rem">${escHtml(formatDateRO(zi) || zi)}${brf ? ` · briefing #${escHtml(brf)}` : ''}</span>
            </div>
            ${motiv ? `<div class="muted" style="font-size:.85rem;margin-top:6px">${escHtml(motiv)}</div>` : ''}
            ${_deciziaPropunerii(w)}
          </div>`;
    }).join('');
}

/** Decizia inginerului pe o propunere din briefing (29 sep 2026) — refuzată cu
 *  motiv, făcută lucrare, sau încă nedecisă (se decide în „De validat"). */
function _deciziaPropunerii(w: FluxRaport): string {
    const cine = w.validated_by ? ` · ${escHtml(w.validated_by)}` : '';
    if (w.validation_status === 'refuzata')
        return `<div style="font-size:.82rem;margin-top:6px;color:var(--red)">✕ Refuzată${cine}: ${escHtml(w.validation_comment || '')}</div>`;
    if (w.validation_status === 'transformata')
        return `<div style="font-size:.82rem;margin-top:6px;color:var(--green)">✓ Făcută lucrare${cine}: ${escHtml(w.validation_comment || '')}</div>`;
    return '<div class="muted" style="font-size:.82rem;margin-top:6px">⏳ Nedecisă — se decide în „De validat" → Propuneri briefing</div>';
}

/* ── Raportul „Săptămâna" (29 sep 2026) ─────────────────────────
   Făcut / restant / de validat pe linie și pe om, într-un singur loc. Definițiile
   vin de la server, odată cu cifrele — ce se numără se vede sub tabel. */
let _saptData: RaportSaptamana | null = null;
let _saptInit = false;

function _saptMuta(zile: number) {
    const el = document.getElementById('sapt-zi') as HTMLInputElement | null;
    if (!el) return;
    const d = new Date((el.value || todayStr()) + 'T12:00:00');
    d.setDate(d.getDate() + zile);
    el.value = localDateKey(d);
    loadRaportSaptamana();
}

export async function loadRaportSaptamana() {
    const ziEl = document.getElementById('sapt-zi') as HTMLInputElement | null;
    const linEl = document.getElementById('sapt-linie') as HTMLSelectElement | null;
    const totEl = document.getElementById('sapt-total');
    const contEl = document.getElementById('sapt-continut');
    if (!ziEl || !linEl || !totEl || !contEl) return;
    if (!_saptInit) {
        _saptInit = true;
        ziEl.value = todayStr();
        await _ensureMachineCache().catch(() => null);   // harta liniilor vine cu lista de utilaje
        _populateLineSelect(linEl, 'Toată fabrica');
        document.getElementById('sapt-aplica')?.addEventListener('click', () => loadRaportSaptamana());
        document.getElementById('sapt-inapoi')?.addEventListener('click', () => _saptMuta(-7));
        document.getElementById('sapt-inainte')?.addEventListener('click', () => _saptMuta(7));
        document.getElementById('sapt-csv')?.addEventListener('click', () => _saptCsv());
    }
    const params = new URLSearchParams({ start: ziEl.value || todayStr() });
    if (linEl.value) params.set('line_id', linEl.value);
    totEl.innerHTML = '<span class="muted">Se calculează…</span>';
    contEl.innerHTML = '';
    let d: RaportSaptamana;
    try {
        d = await api('/api/reports/saptamana?' + params);
    } catch {
        totEl.innerHTML = '';
        contEl.innerHTML = '<div class="empty-state" style="color:var(--red)">Eroare la încărcare raport</div>';
        return;
    }
    _saptData = d;
    const et = document.getElementById('sapt-eticheta');
    if (et) et.textContent = `${d.period.eticheta} · ${d.scope.label}`;
    const t = d.total || {};
    const faraNumaratoare = t.restant === null || t.restant === undefined;
    const rn = d.restante_numarate || {};
    totEl.innerHTML = `
      <div class="stat-card" style="min-width:140px;border-left:3px solid #22c55e"><div class="label">Făcute</div><div class="value" style="color:#22c55e">${t.facut || 0}</div></div>
      <div class="stat-card" style="min-width:140px;border-left:3px solid #ef4444" title="${faraNumaratoare ? 'Nicio seară numărată în săptămâna asta' : 'Seara ' + escHtml(formatDateRO(rn.ultima) || rn.ultima)}"><div class="label">Restante</div><div class="value" style="color:#ef4444">${faraNumaratoare ? '—' : t.restant}</div>
        ${faraNumaratoare ? '<div class="stat-sub" style="font-size:.7rem;opacity:.8">nenumărate (înainte de 29 sep)</div>' : `<div class="stat-sub" style="font-size:.7rem;opacity:.8">${t.restant_nou || 0} noi în săptămână</div>`}</div>
      <div class="stat-card" style="min-width:140px;border-left:3px solid #f59e0b"><div class="label">De validat</div><div class="value" style="color:#f59e0b">${t.de_validat || 0}</div></div>
      <div class="stat-card" style="min-width:140px" title="Doar lucrările cu durată măsurată"><div class="label">Ore măsurate</div><div class="value">${_formatHours(t.minute || 0)}</div></div>`;

    const celula = (v: number | null | undefined) => (v === null || v === undefined) ? '<span class="muted">—</span>' : String(v);
    const linii = (d.pe_linie || []).map((l) => `<tr>
        <td><strong>${escHtml(l.linie)}</strong></td><td>${l.facut}</td>
        <td>${celula(l.restant)}${l.restant_critic ? ` <span style="color:var(--red)" title="critice">(${l.restant_critic} crit.)</span>` : ''}</td>
        <td>${celula(l.restant_nou)}</td><td>${l.de_validat}</td><td>${_formatHours(l.minute || 0)}</td></tr>`).join('');
    const oameni = (d.pe_om || []).map((o) => `<tr>
        <td><strong>${escHtml(o.om)}</strong></td><td>${o.facut}</td><td>${o.de_validat}</td><td>${o.amanari}</td>
        <td>${_formatHours(o.minute || 0)}</td><td>${escHtml((o.linii || []).join(' · '))}</td></tr>`).join('');
    const def = d.definitii || {};
    contEl.innerHTML = `
      <h3 style="margin:8px 0">Pe linie</h3>
      <div class="rpt-table-wrap"><table>
        <thead><tr><th>Linia</th><th>Făcute</th><th>Restante</th><th>Restante noi</th><th>De validat</th><th>Ore măsurate</th></tr></thead>
        <tbody>${linii || '<tr><td colspan="6" class="muted">Nimic în săptămâna asta.</td></tr>'}</tbody></table></div>
      <h3 style="margin:16px 0 8px">Pe om</h3>
      <div class="rpt-table-wrap"><table>
        <thead><tr><th>Cine</th><th>Făcute</th><th>De validat</th><th>Amânări</th><th>Ore măsurate</th><th>Linii</th></tr></thead>
        <tbody>${oameni || '<tr><td colspan="6" class="muted">Nimeni n-a înregistrat lucrări în săptămâna asta.</td></tr>'}</tbody></table></div>
      <details style="margin-top:12px"><summary class="muted">Ce se numără</summary>
        <ul style="font-size:.85rem;line-height:1.5">${Object.entries(def).map(([k, v]) => `<li><b>${escHtml(k.replace('_', ' '))}</b>: ${escHtml(String(v))}</li>`).join('')}</ul>
      </details>`;
}

function _saptCsv() {
    const d = _saptData;
    if (!d) { toast('Calculează întâi raportul.', 'warning'); return; }
    const rows: string[][] = [['Săptămâna', d.period.eticheta], ['Filtru', d.scope.label], [],
        ['Linia', 'Făcute', 'Restante', 'Restante noi', 'Restante critice', 'De validat', 'Minute măsurate']];
    (d.pe_linie || []).forEach((l) => rows.push([l.linie, String(l.facut), String(l.restant ?? ''), String(l.restant_nou ?? ''),
        String(l.restant_critic ?? ''), String(l.de_validat), String(l.minute || 0)]));
    rows.push([], ['Cine', 'Făcute', 'De validat', 'Amânări', 'Minute măsurate', 'Linii']);
    (d.pe_om || []).forEach((o) => rows.push([o.om, String(o.facut), String(o.de_validat), String(o.amanari),
        String(o.minute || 0), (o.linii || []).join(' ')]));
    _downloadCsv(rows, `saptamana_${d.period.start}_${d.period.end}.csv`);
}

/* ── Per-technician execution report (A.6, 2026-05-13) ─────────── */

let _lastUserComplianceData: ConformitatePeOm | null = null;

// REPARAT 19 aug 2026 (audit, constatarea 2.12 + recenzia lotului 1).
// `toISOString()` dă ziua UTC. Pentru _todayIso greșeala apărea doar între
// 00:00 și 03:00; pentru _firstDayOfMonthIso era greșită ÎNTOTDEAUNA:
// `new Date(an, luna, 1)` e miezul nopții LOCAL, iar România e mereu înaintea
// UTC (+2/+3), deci ziua 1 devenea mereu ultima zi a lunii precedente —
// „1 aug" ieșea „2026-07-31". De aici raportul lunar către conducere umflat
// cu 57%: presetul „luna aceasta" începea cu o zi mai devreme și înghițea
// ultima zi a lunii trecute.
function _todayIso(): string {
    return todayStr();
}

function _firstDayOfMonthIso(d: Date): string {
    return localDateKey(new Date(d.getFullYear(), d.getMonth(), 1));
}

function _applyUserCompliancePreset(preset: string) {
    const fromEl = document.getElementById('user-compliance-from') as HTMLInputElement | null;
    const toEl = document.getElementById('user-compliance-to') as HTMLInputElement | null;
    if (!fromEl || !toEl) return;
    const today = new Date();
    if (preset === 'this_month') {
        fromEl.value = _firstDayOfMonthIso(today);
        toEl.value = _todayIso();
    } else if (preset === 'last_month') {
        const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastPrev = new Date(firstThis.getTime() - 86400000);
        const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
        fromEl.value = localDateKey(firstPrev);
        toEl.value = localDateKey(lastPrev);
    } else if (preset === 'last_30') {
        const start = new Date(today.getTime() - 29 * 86400000);
        fromEl.value = localDateKey(start);
        toEl.value = _todayIso();
    }
    document.querySelectorAll('.report-preset-btn').forEach(btn => {
        btn.classList.toggle('btn-active', (btn as HTMLElement).dataset.preset === preset);
    });
}

function _initUserComplianceFilters(structure: FactoryStructure | null) {
    const machineSel = document.getElementById('user-compliance-machine') as HTMLSelectElement | null;
    if (machineSel && structure && structure.production_lines) {
        machineSel.innerHTML = '<option value="">Toate</option>';
        const ids = new Set<string>();
        structure.production_lines.forEach((pl) => {
            ids.add(pl.id);
            (pl.equipment || []).forEach((eq) => { ids.add(eq.id); });
        });
        [...ids].sort().forEach(mid => {
            machineSel.innerHTML += `<option value="${escHtml(mid)}">${escHtml(mid)}</option>`;
        });
        _insertLineSelectBefore(machineSel, 'user-compliance-line', 'Toate liniile', 'Toate');
    }
    _applyUserCompliancePreset('this_month');

    document.querySelectorAll('.report-preset-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const preset = (btn as HTMLElement).dataset.preset || 'this_month';
            if (preset === 'custom') {
                document.querySelectorAll('.report-preset-btn').forEach(b => b.classList.remove('btn-active'));
                btn.classList.add('btn-active');
                return;
            }
            _applyUserCompliancePreset(preset);
            loadUserComplianceReport();
        });
    });

    document.getElementById('user-compliance-apply')?.addEventListener('click', () => loadUserComplianceReport());
    document.getElementById('user-compliance-reset')?.addEventListener('click', () => {
        const userSel = document.getElementById('user-compliance-user') as HTMLSelectElement | null;
        if (userSel) userSel.value = '';
        const ul = document.getElementById('user-compliance-line') as HTMLSelectElement | null;
        if (ul) { ul.value = ''; ul.dispatchEvent(new Event('change')); }
        if (machineSel) machineSel.value = '';
        _applyUserCompliancePreset('this_month');
        loadUserComplianceReport();
    });
    document.getElementById('user-compliance-export-csv')?.addEventListener('click', () => _exportUserComplianceCsv());
}

function _initialColor(name: string): string {
    const palette = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4', '#ec4899', '#14b8a6', '#f97316'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return palette[h % palette.length];
}

function _formatHours(minutes: number): string {
    if (!minutes || minutes <= 0) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
}

function _statusBadge(status: string): string {
    if (status === 'done') {
        return '<span class="report-status-badge" style="background:rgba(34,197,94,.18);color:#22c55e">✓ Făcut</span>';
    }
    if (status === 'postponed') {
        return '<span class="report-status-badge" style="background:rgba(245,158,11,.18);color:#f59e0b">⏳ Amânat</span>';
    }
    if (status === 'missed') {
        // 3 aug 2026: se numea «Ratat», dar sursa e validation_status='rejected' —
        // lucrarea A FOST făcută și respinsă de inginer, nu ratată. Eticheta veche
        // citea ca «n-a făcut-o» și pica nedrept pe tehnician.
        return '<span class="report-status-badge" style="background:rgba(239,68,68,.18);color:#ef4444" '
             + 'title="Lucrarea a fost efectuată, dar respinsă de inginer la validare">↺ Respins</span>';
    }
    return `<span class="report-status-badge">${escHtml(status)}</span>`;
}

export async function loadUserComplianceReport() {
    const fromV = (document.getElementById('user-compliance-from') as HTMLInputElement | null)?.value || '';
    const toV = (document.getElementById('user-compliance-to') as HTMLInputElement | null)?.value || '';
    const userV = (document.getElementById('user-compliance-user') as HTMLSelectElement | null)?.value || '';
    const machineV = (document.getElementById('user-compliance-machine') as HTMLSelectElement | null)?.value || '';
    const lineV = (document.getElementById('user-compliance-line') as HTMLSelectElement | null)?.value || '';

    const params = new URLSearchParams();
    if (fromV) params.set('start', fromV);
    if (toV) params.set('end', toV);
    if (userV) params.set('user', userV);
    if (machineV) params.set('machine_id', machineV);
    else if (lineV) params.set('line_id', lineV);

    const summaryEl = document.getElementById('user-compliance-summary');
    const cardsEl = document.getElementById('user-compliance-cards');
    if (!summaryEl || !cardsEl) return;
    summaryEl.innerHTML = '<span class="muted">Se calculează…</span>';
    cardsEl.innerHTML = '';

    let data: ConformitatePeOm;
    try {
        data = await api(`/api/reports/compliance/by-user?${params}`);
    } catch (e) {
        summaryEl.innerHTML = '';
        cardsEl.innerHTML = '<div class="empty-state" style="color:var(--red)">Eroare la încărcare raport</div>';
        return;
    }
    _lastUserComplianceData = data;

    const userSel = document.getElementById('user-compliance-user') as HTMLSelectElement | null;
    if (userSel) {
        const current = userSel.value;
        const opts: string[] = ['<option value="">Toți tehnicienii</option>'];
        (data.users_available || []).forEach((u: string) => {
            opts.push(`<option value="${escHtml(u)}">${escHtml(u)}</option>`);
        });
        userSel.innerHTML = opts.join('');
        if (current) userSel.value = current;
    }

    const summary = data.summary || {};
    const sevColor = (pct: number) => pct >= 90 ? '#22c55e' : pct >= 70 ? '#f59e0b' : pct >= 50 ? '#f97316' : '#ef4444';
    summaryEl.innerHTML = `
      <div class="stat-card" style="min-width:140px"><div class="label">Tehnicieni activi</div><div class="value">${summary.users_count || 0}</div></div>
      <div class="stat-card" style="min-width:140px"><div class="label">Lucrări planificate</div><div class="value">${summary.total_planned || 0}</div></div>
      <div class="stat-card" style="min-width:140px;border-left:3px solid #22c55e"><div class="label">Făcute</div><div class="value" style="color:#22c55e">${summary.total_done || 0}</div></div>
      <div class="stat-card" style="min-width:140px;border-left:3px solid #f59e0b"><div class="label">Amânate</div><div class="value" style="color:#f59e0b">${summary.total_postponed || 0}</div></div>
      <div class="stat-card" style="min-width:140px;border-left:3px solid #ef4444" title="Lucrări efectuate, dar respinse de inginer la validare"><div class="label">Respinse la validare</div><div class="value" style="color:#ef4444">${summary.total_missed || 0}</div></div>
      <div class="stat-card" style="min-width:140px" title="Se însumează doar lucrările cu durată măsurată. Restul au fost bifate fără cronometru, deci timpul lor nu se știe.">
        <div class="label">Total ore lucrate</div>
        <div class="value">${_formatHours(summary.total_minutes || 0)}</div>
        ${summary.total_done ? `<div class="stat-sub" style="font-size:.7rem;opacity:.8">măsurat pe ${summary.total_cu_durata || 0} din ${summary.total_done} lucrări</div>` : ''}
      </div>
      <div class="stat-card" style="min-width:140px"><div class="label">Compliance global</div><div class="value" style="color:${sevColor(summary.compliance_pct || 0)}">${summary.compliance_pct || 0}%</div></div>
    `;

    const perUser = data.per_user || [];
    if (!perUser.length) {
        cardsEl.innerHTML = '<div class="empty-state" style="padding:24px;text-align:center">Nu există activitate de mentenanță pentru filtrele selectate.</div>';
        return;
    }

    // Taburi per tehnician (cerere Dan, 3 aug 2026): cu 4+ oameni și zeci de
    // lucrări fiecare, cardurile unul sub altul făceau pagina interminabilă.
    // Acum: un rând de taburi cu informația esențială, deschis unul singur.
    if (!perUser.some((u) => u.user === _rptSelectedUser)) {
        _rptSelectedUser = perUser[0].user;
    }
    cardsEl.innerHTML =
        '<div class="rpt-user-tabs" role="tablist" aria-label="Tehnicieni">'
        + perUser.map((u, idx: number) => _renderUserTab(u, idx)).join('')
        + '</div><div id="user-compliance-detail"></div>';

    cardsEl.querySelectorAll('.rpt-user-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            _rptSelectedUser = (btn as HTMLElement).dataset.user || '';
            _renderSelectedUser(perUser);
        });
    });
    _renderSelectedUser(perUser);
}

let _rptSelectedUser = '';

function _renderSelectedUser(perUser: OmConformitate[]) {
    const detailEl = document.getElementById('user-compliance-detail');
    if (!detailEl) return;
    document.querySelectorAll('.rpt-user-tab').forEach(b => {
        const on = (b as HTMLElement).dataset.user === _rptSelectedUser;
        b.classList.toggle('active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const idx = perUser.findIndex(u => u.user === _rptSelectedUser);
    const u = idx >= 0 ? perUser[idx] : perUser[0];
    if (!u) { detailEl.innerHTML = ''; return; }
    detailEl.innerHTML = _renderUserCard(u, Math.max(idx, 0));
    const tableEl = detailEl.querySelector('table.report-detail-table') as HTMLTableElement | null;
    if (!tableEl) return;
    tableEl.querySelectorAll('th[data-sort]').forEach(thEl => {
        thEl.addEventListener('click', () => {
            _sortDetailTable(tableEl, (thEl as HTMLElement).dataset.sort || '');
        });
    });
}

function _renderUserTab(u: OmConformitate, idx: number): string {
    const stats = u.stats || {};
    const compliance = stats.compliance_pct || 0;
    const sevColor = compliance >= 90 ? '#22c55e' : compliance >= 70 ? '#f59e0b' : compliance >= 50 ? '#f97316' : '#ef4444';
    const initial = (u.user || '?').trim().charAt(0).toUpperCase() || '?';
    const avatarColor = _initialColor(u.user || `user${idx}`);
    const alerte: string[] = [];
    if (stats.missed) alerte.push(`<span style="color:#ef4444" title="Respinse la validare">↺${stats.missed}</span>`);
    if (stats.postponed) alerte.push(`<span style="color:#f59e0b" title="Amânate">⏳${stats.postponed}</span>`);
    return `
      <button type="button" class="rpt-user-tab" role="tab" data-user="${escHtml(u.user)}" aria-selected="false">
        <span class="rpt-user-tab-avatar" style="background:${avatarColor}">${escHtml(initial)}</span>
        <span class="rpt-user-tab-txt">
          <span class="rpt-user-tab-name">${escHtml(u.user)}${u.role ? ` <em>${escHtml(u.role)}</em>` : ''}</span>
          <span class="rpt-user-tab-meta">${stats.done || 0} făcute · ${_formatHours(stats.minutes || 0)}${alerte.length ? ' · ' + alerte.join(' ') : ''}</span>
        </span>
        <span class="rpt-user-tab-pct" style="color:${sevColor}">${compliance}%</span>
      </button>`;
}

function _renderUserCard(u: OmConformitate, idx: number): string {
    const stats = u.stats || {};
    const compliance = stats.compliance_pct || 0;
    const sevColor = compliance >= 90 ? '#22c55e' : compliance >= 70 ? '#f59e0b' : compliance >= 50 ? '#f97316' : '#ef4444';
    const initial = (u.user || '?').trim().charAt(0).toUpperCase() || '?';
    const avatarColor = _initialColor(u.user || `user${idx}`);
    const roleLabel = u.role ? `<span class="muted" style="font-size:.72rem;padding:2px 8px;border-radius:8px;background:rgba(255,255,255,.06);margin-left:6px">${escHtml(u.role)}</span>` : '';

    const tasksHtml = (u.tasks || []).map((t) => {
        const dur = t.duration_min ? _formatHours(t.duration_min) : '—';
        const dateMain = t.done_date || t.planned_date || '';
        const extraDate = (t.status === 'postponed' && t.postponed_until)
            ? `<br><span class="muted" style="font-size:.7rem">→ ${escHtml(t.postponed_until)}</span>`
            : '';
        const validation = t.validation_status === 'rejected'
            ? '<span style="color:#ef4444;font-size:.7rem">respinsă</span>'
            : t.validation_status === 'approved'
                ? '<span style="color:#22c55e;font-size:.7rem">validată</span>'
                : t.validation_status
                    ? `<span class="muted" style="font-size:.7rem">${escHtml(t.validation_status)}</span>`
                    : '';
        return `
          <tr data-status="${escHtml(t.status)}" data-machine="${escHtml(t.machine_id)}" data-date="${escHtml(t.sort_date || dateMain)}" data-duration="${t.duration_min || 0}">
            <td><strong style="font-size:.82rem">${escHtml(t.machine_id || '—')}</strong>${t.line ? `<br><span class="muted" style="font-size:.68rem">Linia ${escHtml(t.line)}</span>` : ''}</td>
            <td style="font-size:.82rem">${escHtml(truncate(t.action || '', 80))}</td>
            <td style="font-size:.78rem;white-space:nowrap">${escHtml(dateMain || '—')}${extraDate}</td>
            <td>${_statusBadge(t.status)}</td>
            <td style="text-align:right;font-size:.8rem">${dur}</td>
            <td>${validation}</td>
          </tr>
        `;
    }).join('');

    return `
      <div class="report-user-card" data-user="${escHtml(u.user)}">
        <div class="report-user-card-head">
          <div class="report-user-avatar" style="background:${avatarColor}">${escHtml(initial)}</div>
          <div style="flex:1;min-width:0">
            <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap"><strong style="font-size:1.05rem">${escHtml(u.user)}</strong>${roleLabel}</div>
            <div class="muted" style="font-size:.78rem;margin-top:2px">${stats.planned} lucrări planificate · ${_formatHours(stats.minutes)} lucrate</div>
          </div>
          <div class="report-user-compliance" style="color:${sevColor}">
            <div style="font-size:1.6rem;font-weight:800;line-height:1">${compliance}%</div>
            <div class="muted" style="font-size:.7rem;text-transform:uppercase;letter-spacing:.05em">compliance</div>
          </div>
        </div>
        <div class="report-user-counters">
          <div class="report-counter" style="border-color:#22c55e"><span style="color:#22c55e">✓</span><strong>${stats.done}</strong><span class="muted">făcute</span></div>
          <div class="report-counter" style="border-color:#f59e0b"><span style="color:#f59e0b">⏳</span><strong>${stats.postponed}</strong><span class="muted">amânate</span></div>
          <div class="report-counter" style="border-color:#ef4444" title="Efectuate, dar respinse de inginer la validare"><span style="color:#ef4444">↺</span><strong>${stats.missed}</strong><span class="muted">respinse</span></div>
          <div class="report-counter" style="border-color:#3b82f6"><span style="color:#3b82f6">🔧</span><strong>${_formatHours(stats.minutes)}</strong><span class="muted">ore</span></div>
        </div>
        <div class="report-detail-wrap">
          <table class="report-detail-table">
            <thead><tr>
              <th data-sort="machine">Mașină ▾</th>
              <th>Lucrare</th>
              <th data-sort="date">Dată ▾</th>
              <th data-sort="status">Status ▾</th>
              <th data-sort="duration" style="text-align:right">Durată ▾</th>
              <th>Verificare</th>
            </tr></thead>
            <tbody>${tasksHtml || `<tr><td colspan="6" class="empty-state" style="padding:14px">Nicio lucrare în perioadă.</td></tr>`}</tbody>
          </table>
        </div>
      </div>
    `;
}

const _detailSortState: WeakMap<HTMLTableElement, { key: string; asc: boolean }> = new WeakMap();

function _sortDetailTable(tableEl: HTMLTableElement, key: string) {
    const tbody = tableEl.querySelector('tbody');
    if (!tbody) return;
    const rows = Array.from(tbody.querySelectorAll('tr')).filter(r => !r.querySelector('.empty-state'));
    if (!rows.length) return;
    const prev = _detailSortState.get(tableEl);
    const asc = prev && prev.key === key ? !prev.asc : false;
    _detailSortState.set(tableEl, { key, asc });
    rows.sort((a, b) => {
        const va = (a.getAttribute(`data-${key}`) || '').toLowerCase();
        const vb = (b.getAttribute(`data-${key}`) || '').toLowerCase();
        if (key === 'duration') {
            const na = parseInt(va, 10) || 0;
            const nb = parseInt(vb, 10) || 0;
            return asc ? na - nb : nb - na;
        }
        if (va < vb) return asc ? -1 : 1;
        if (va > vb) return asc ? 1 : -1;
        return 0;
    });
    rows.forEach(r => tbody.appendChild(r));
}

function _exportUserComplianceCsv() {
    if (!_lastUserComplianceData || !(_lastUserComplianceData.per_user || []).length) {
        toast('Nu există date de exportat. Calculează raportul mai întâi.');
        return;
    }
    const rows: string[][] = [
        ['Tehnician', 'Rol', 'Linie', 'Utilaj', 'Dată planificată', 'Dată efectuată',
         'Status', 'Durată (min)', 'Validare', 'Lucrare'],
    ];
    (_lastUserComplianceData.per_user || []).forEach((u) => {
        (u.tasks || []).forEach((t) => {
            rows.push([
                u.user || '', u.role || '', t.line || '', t.machine_id || '',
                t.planned_date || '', t.done_date || '', t.status || '',
                String(t.duration_min || 0), t.validation_status || '',
                t.action || '',
            ]);
        });
    });
    const period = _lastUserComplianceData.period || {};
    _downloadCsv(rows, `executie_per_tehnician_${period.start || 'acum'}_${period.end || 'acum'}.csv`);
    toast(`${rows.length - 1} rânduri exportate`, 'success');
}

/* ── Export CSV pentru jurnal și workflow-uri (3 aug 2026) ──────
   Exportă TOT setul filtrat, nu doar pagina afișată. */
function _downloadCsv(rows: string[][], filename: string) {
    /* 5 sep 2026 (Dan, cu fișierul deschis în Excel: «sunt de neînțeles»):
       exportul despărțea coloanele cu VIRGULĂ, iar Excel-ul românesc desparte
       pe PUNCT ȘI VIRGULĂ — tot rândul ateriza într-o singură celulă, cu
       ghilimele cu tot. Scriem separatorul și în prima linie (`sep=;`), pe care
       Excel o citește ca instrucțiune și n-o afișează; LibreOffice o respectă la
       fel. Marca de octeți (BOM) rămâne, altfel diacriticele ies stricate. */
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = 'sep=;\n' + rows.map(r => r.map(esc).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
}

async function _exportJournalCsv() {
    const params = _journalFilterParams();
    params.set('limit', '10000');
    let data: RaportJurnal;
    try {
        data = await api(`/api/reports/maintenance?${params}`);
    } catch {
        toast('Eroare la export', 'error');
        return;
    }
    const entries = data.entries || [];
    if (!entries.length) { toast('Nu există înregistrări de exportat.'); return; }
    /* Ordinea coloanelor e cea în care se citește un raport: întâi identitatea
       lucrării (număr, dată, linie, utilaj), apoi cine și cum s-a validat, apoi
       trimiterea la MANUAL (documentul și pagina din care vine cerința — cerută
       pentru ISO), iar textele lungi la sfârșit, ca primele coloane să rămână
       citibile pe ecran. */
    const rows: string[][] = [
        ['Nr. înregistrare', 'Data', 'Linie', 'Utilaj (ID)', 'Utilaj (nume)', 'Tip lucrare', 'Categorie',
         'Executant', 'Validare', 'Validat de', 'Data validării', 'Început', 'Terminat',
         'Durată (min)', 'Interval manual (ID)', 'Cadență din manual', 'Manual (fișier)',
         'Pagina', 'Piese', 'Acțiune', 'Note', 'Baseline GO-LIVE'],
    ];
    entries.forEach((e) => rows.push([
        e.registration_number || '', e.date || '',
        e.line_efectiv || e.line_id || '', e.machine_id || '', e.machine_name || '',
        (e.tip_eticheta || e.tip || ''), e.category || '', e.technician || '', e.validation_status || '',
        e.validated_by || '', (e.validation_date || '').slice(0, 16),
        (e.start_time || '').slice(0, 16), (e.completed_time || '').slice(0, 16),
        String(e.duration_minutes ?? ''),
        e.interval_id ? String(e.interval_id) : '', e.interval_manual || '',
        e.manual_filename || '', e.manual_page ? String(e.manual_page) : '',
        e.parts_used || '', e.action || '', e.notes || '',
        e.synthetic ? 'DA' : '',
    ]));
    _downloadCsv(rows, `jurnal_mentenanta_${todayStr()}.csv`);
    toast(`${entries.length} înregistrări exportate`, 'success');
}

async function _exportWorkflowsCsv() {
    const params = _wfFilterParams();
    params.set('limit', '1000');
    params.set('meta', '1');
    let resp: ListaFluxuri;
    try {
        resp = await api(`/api/maintenance_workflows?${params}`);
    } catch {
        toast('Eroare la export', 'error');
        return;
    }
    const items = Array.isArray(resp) ? resp : (resp?.items || []);
    if (!items.length) { toast('Nu există workflow-uri de exportat.'); return; }
    const rows: string[][] = [
        ['ID', 'Început', 'Terminat', 'Utilaj (ID)', 'Utilaj (nume)', 'Operator',
         'Pași făcuți', 'Pași total', 'Durată (min)', 'Status', 'Validat de',
         'Interval (ID)', 'Acțiune', 'Comentariu validare'],
    ];
    items.forEach((w) => rows.push([
        String(w.id ?? ''), (w.started_at || '').slice(0, 16), (w.completed_at || '').slice(0, 16),
        w.machine_id || '', w.machine_name || '', w.operator_name || '',
        String(w.steps_completed ?? ''), String(w.steps_total ?? ''), String(w.duration_minutes ?? ''),
        w.status || '', w.validated_by || '',
        w.interval_id ? String(w.interval_id) : '',
        w.action_ro || '', w.validation_comment || '',
    ]));
    _downloadCsv(rows, `workflowuri_mentenanta_${todayStr()}.csv`);
    toast(`${items.length} workflow-uri exportate`, 'success');
}

let _lastComplianceData: RaportConformitate | null = null;

export async function loadComplianceReport() {
    const fromV = (document.getElementById('compliance-from') as HTMLInputElement | null)?.value || '';
    const toV = (document.getElementById('compliance-to') as HTMLInputElement | null)?.value || '';
    const mid = (document.getElementById('compliance-machine') as HTMLSelectElement | null)?.value || '';
    const lineV = (document.getElementById('compliance-line') as HTMLSelectElement | null)?.value || '';
    const params = new URLSearchParams();
    if (fromV) params.set('start', fromV);
    if (toV) params.set('end', toV);
    if (mid) params.set('machine_id', mid);
    else if (lineV) params.set('line_id', lineV);
    // 12 sep 2026 (Dan: «raport cu toate scadențele»): compliance-ul acoperă acum
    // și cadențele pe ore (180h, 360h…), socotite pe contorul mașinii. Selectorul
    // alege pe ce se uită raportul: calendar, ore, sau amândouă.
    const felV = (document.getElementById('compliance-fel') as HTMLSelectElement | null)?.value || 'toate';
    if (felV && felV !== 'toate') params.set('fel', felV);

    const summaryEl = document.getElementById('compliance-summary');
    const tbody = document.querySelector('#compliance-table tbody') as HTMLElement | null;
    if (!tbody) return;
    if (summaryEl) summaryEl.innerHTML = '<span class="muted">Se calculează…</span>';
    tbody.innerHTML = '<tr><td colspan="8" class="empty-state">Se calculează…</td></tr>';

    let data: RaportConformitate;
    try {
        data = await api(`/api/reports/compliance?${params}`);
    } catch (e) {
        tbody.innerHTML = '<tr><td colspan="8" class="empty-state" style="color:var(--red)">Eroare la încărcare</td></tr>';
        return;
    }
    _lastComplianceData = data;

    const items = data.items || [];
    const canAssign = ['engineer', 'master'].includes(state.currentUser?.role || '');
    const summary = data.summary || {};
    if (summaryEl) {
        const sevColor = (s: string) => s === 'critical' ? '#ef4444' : s === 'warning' ? '#f59e0b' : '#22c55e';
        summaryEl.innerHTML = `
          <div class="stat-card" style="min-width:130px"><div class="label">Total lucrări recurente</div><div class="value">${summary.total || 0}</div></div>
          <div class="stat-card" style="min-width:130px;border-left:3px solid #22c55e"><div class="label">Compliance OK (≥90%)</div><div class="value" style="color:#22c55e">${summary.ok || 0}</div></div>
          <div class="stat-card" style="min-width:130px;border-left:3px solid #f59e0b"><div class="label">Atenție (50-89%)</div><div class="value" style="color:#f59e0b">${summary.warning || 0}</div></div>
          <div class="stat-card" style="min-width:130px;border-left:3px solid #ef4444"><div class="label">Critic (&lt;50%)</div><div class="value" style="color:#ef4444">${summary.critical || 0}</div></div>
          <div class="stat-card" style="min-width:130px"><div class="label">Compliance mediu</div><div class="value" style="color:${sevColor(summary.avg_compliance >= 90 ? 'ok' : summary.avg_compliance >= 50 ? 'warning' : 'critical')}">${summary.avg_compliance || 0}%</div></div>
        `;
    }

    if (!items.length) {
        tbody.innerHTML = '<tr><td colspan="8" class="empty-state">Nu există lucrări recurente în această perioadă (sau nu s-au generat încă suficiente date).</td></tr>';
        return;
    }

    tbody.innerHTML = '';
    items.forEach((it) => {
        const sev = it.severity || 'ok';
        const sevColor = sev === 'critical' ? '#ef4444' : sev === 'warning' ? '#f59e0b' : '#22c55e';
        const sevBg = sev === 'critical' ? 'rgba(239,68,68,.15)' : sev === 'warning' ? 'rgba(245,158,11,.15)' : 'rgba(34,197,94,.12)';
        const cadenceLabel = it.cadenta_fel === 'ore'
            ? `${it.interval_text || (it.interval_hours + 'h')} · mașina a mers ${it.ore_functionare_perioada ?? '?'}h`
            : (it.cal_days === 1 ? 'zilnic' : it.cal_days === 7 ? 'săptămânal' : it.cal_days === 30 ? 'lunar' : `${it.cal_days} zile`);
        const critBadge = it.critical ? ' <span style="color:#ef4444;font-weight:700" title="Lucrare critică">★</span>' : '';
        // 28 oct 2026: insigna arată cât a stat linia în perioadă (orice oprire de peste o oră),
        // timp scos din cerut — ca în plan, unde scadența se mută cu opririle
        const pauseBadge = (it.pause_days_excluded && it.pause_days_excluded > 0)
            ? ` <span class="badge-technical-pause" style="font-size:.65rem;margin-left:6px" title="Linia a stat ${it.pause_days_excluded} zile în perioadă (${it.ore_oprita ?? '?'} h, opriri de peste o oră) — timpul acesta nu se cere">⏸ ${it.pause_days_excluded}z oprită</span>`
            : '';
        const tr = document.createElement('tr');
        tr.style.background = sevBg;
        tr.innerHTML = `
          <td><strong>${escHtml(it.machine_name || it.machine_id)}</strong>${pauseBadge}${it.line ? `<br><span class="muted" style="font-size:.7rem">Linia ${escHtml(it.line)}</span>` : ''}</td>
          <td>${escHtml(it.action_ro || '')}${critBadge}</td>
          <td><span style="font-size:.78rem;padding:2px 8px;border-radius:8px;background:rgba(255,255,255,.06)">${escHtml(cadenceLabel)}</span></td>
          <td style="text-align:center;font-weight:600">${it.expected}</td>
          <td style="text-align:center;font-weight:600">${it.completed}</td>
          <td style="text-align:center"><span style="font-size:.95rem;font-weight:700;color:${sevColor}">${it.compliance_pct}%</span></td>
          <td style="font-size:.82rem;color:${sevColor}"><strong style="text-transform:uppercase;font-size:.7rem">${sev}</strong> · ${escHtml(it.recommendation || '')}</td>
          <td style="white-space:nowrap">${canAssign && it.interval_id
            ? `<button type="button" class="btn btn-sm rpt-assign-btn" data-ivid="${it.interval_id}"
                 data-machine="${escHtml(it.machine_id || '')}" data-action="${escHtml(it.action_ro || '')}"
                 title="Alocă lucrarea unui tehnician, cu termen" style="background:#6366f1;color:#fff;border:none">👤 Alocă</button>`
            : ''}</td>
        `;
        tbody.appendChild(tr);
    });

    // 8 aug 2026 (audit UX, punctul 1): din Rapoarte se vedea catastrofa, dar nu
    // se putea face nimic cu ea. Butonul deschide exact dialogul de alocare din
    // calendar — aceeași lucrare, același flux, fără drum până în altă secțiune.
    tbody.querySelectorAll('.rpt-assign-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const el = btn as HTMLElement;
            _openAssignDialog(parseInt(el.dataset.ivid || '0'), el.dataset.machine || '', el.dataset.action || '');
        });
    });

    // Notă globală pentru liniile care au stat în perioadă
    const pausedItems = items.filter((it) => it.pause_days_excluded && it.pause_days_excluded > 0);
    if (pausedItems.length > 0 && summaryEl) {
        const note = document.createElement('div');
        note.className = 'compliance-pause-note';
        note.style.cssText = 'margin-top:10px;padding:10px 14px;background:rgba(245,158,11,.10);border-left:4px solid #f59e0b;border-radius:6px;font-size:.85rem;color:#fbbf24';
        const machinesAffected = Array.from(new Set(pausedItems.map((it) => it.machine_id)));
        const totalPauseDays = pausedItems.reduce((s: number, it) => Math.max(s, it.pause_days_excluded || 0), 0);
        note.innerHTML = `⏸ <strong>Linii oprite în perioadă:</strong> la ${pausedItems.length} lucrări, cerutul s-a socotit doar pe zilele în care linia a mers (până la ${totalPauseDays} zile scoase), pentru ${machinesAffected.length} utilaje: ${machinesAffected.map(m => escHtml(m as string)).join(', ')}. Contează orice oprire a liniei de peste o oră, la fel ca la scadențele din plan; matrița și auxiliarele liniei stau odată cu presa.`;
        summaryEl.appendChild(note);
    }
}

function exportComplianceCsv() {
    if (!_lastComplianceData || !_lastComplianceData.items?.length) {
        toast('Nu există date de exportat. Calculează raportul mai întâi.');
        return;
    }
    /* Coloanele merg de la «ce cere manualul» spre «ce s-a făcut», cu pagina din
       manual lângă cerință — un auditor ISO trebuie să poată deschide sursa.
       Textele lungi (acțiunea și recomandarea) stau la final. */
    const rows: string[][] = [
        ['Linie', 'Utilaj (ID)', 'Utilaj (nume)', 'Categorie', 'Critică', 'Cadență (text)',
         'Cadență (zile)', 'Fel cadență', 'Ore funcționare în perioadă',
         'Manual — pagina', 'Așteptat', 'Efectuat', 'Compliance (%)',
         'Severitate', 'Interval (ID)', 'Acțiune din manual', 'Recomandare'],
    ];
    _lastComplianceData.items.forEach((it) => {
        rows.push([
            it.line || '', it.machine_id || '', it.machine_name || '',
            it.category || '', it.critical ? 'DA' : '',
            it.interval_text || '', String(it.cal_days ?? ''),
            it.cadenta_fel || '', it.ore_functionare_perioada != null ? String(it.ore_functionare_perioada) : '',
            it.manual_page ? String(it.manual_page) : '',
            String(it.expected ?? 0), String(it.completed ?? 0), String(it.compliance_pct ?? 0),
            it.severity || '', it.interval_id ? String(it.interval_id) : '',
            it.action_ro || '', it.recommendation || '',
        ]);
    });
    const period = _lastComplianceData.period || {};
    _downloadCsv(rows, `conformitate_manual_${period.start || 'acum'}_${period.end || 'acum'}.csv`);
    toast(`${_lastComplianceData.items.length} rânduri exportate`, 'success');
}

/* ── Jurnal: perioadă + paginare (3 aug 2026) ───────────────────
   Jurnalul încărca TOT istoricul la fiecare deschidere și afișa fără paginare
   ce încăpea în plafonul serverului (2000) — la ritmul actual devenea trunchiere
   invizibilă în câteva luni. Acum: perioadă implicită + pagini de 100. */
const _RPT_PER_PAGE = 100;
let _rptJournalPage = 1;
let _rptJournalFilterKey = '';

const _TIP_SCURT: Record<string, string> = {
    planificata: 'plan', revizie: 'revizie', urgenta: 'URGENT', ad_hoc: 'ad-hoc',
};

function _journalFilterParams(): URLSearchParams {
    const params = new URLSearchParams();
    /* 5 sep 2026 (semnalat de Dan): selectorul de LINIE doar rescria lista de
       utilaje din al doilea select. Dacă lăsai «Toate», cererea pleca fără
       filtru și raportul arăta toată fabrica — filtrul părea stricat, de fapt
       nu exista. Linia merge acum la server ca `line_id`; utilajul, mai
       specific, primează (regula e aceeași și pe server). */
    const line = (document.getElementById('reports-line') as HTMLSelectElement | null)?.value || '';
    const machine = ($('#reports-machine') as HTMLInputElement)?.value;
    if (line && !machine) params.set('line_id', line);
    const category = ($('#reports-category') as HTMLInputElement)?.value;
    const from = ($('#reports-from') as HTMLInputElement)?.value;
    const to = ($('#reports-to') as HTMLInputElement)?.value;
    const showSynthetic = (document.getElementById('reports-show-synthetic') as HTMLInputElement | null)?.checked === true;
    if (machine) params.set('machine_id', machine);
    if (category) params.set('category', category);
    // 12 sep 2026: felul lucrării (din manual / revizie / urgentă / ad-hoc),
    // derivat pe server din coloane, nu din categoria liberă.
    const tip = (document.getElementById('reports-tip') as HTMLSelectElement | null)?.value || '';
    if (tip) params.set('tip', tip);
    if (from) params.set('from_date', from);
    if (to) params.set('to_date', to);
    if (!showSynthetic) params.set('include_synthetic', '0');
    return params;
}

function _applyJournalPreset(preset: string) {
    const fromEl = document.getElementById('reports-from') as HTMLInputElement | null;
    const toEl = document.getElementById('reports-to') as HTMLInputElement | null;
    if (!fromEl || !toEl) return;
    const today = new Date();
    if (preset === 'all') {
        fromEl.value = '';
        toEl.value = '';
    } else if (preset === 'this_month') {
        fromEl.value = _firstDayOfMonthIso(today);
        toEl.value = _todayIso();
    } else if (preset === 'this_year') {
        fromEl.value = `${today.getFullYear()}-01-01`;
        toEl.value = _todayIso();
    } else { // last_30
        const d = new Date(today);
        d.setDate(d.getDate() - 30);
        fromEl.value = localDateKey(d);
        toEl.value = _todayIso();
    }
    document.querySelectorAll('.rpt-journal-preset').forEach(b => {
        b.classList.toggle('btn-active', (b as HTMLElement).dataset.preset === preset);
    });
}

export async function loadReportsData(page?: number) {
    const params = _journalFilterParams();
    const showSynthetic = params.get('include_synthetic') !== '0';

    // Schimbarea filtrelor readuce la pagina 1 — altfel „pagina 7" pe un set nou
    // de rezultate arată gol și pare că s-au pierdut date.
    const filterKey = params.toString();
    if (filterKey !== _rptJournalFilterKey) {
        _rptJournalFilterKey = filterKey;
        _rptJournalPage = 1;
    }
    if (page && page >= 1) _rptJournalPage = page;
    params.set('limit', String(_RPT_PER_PAGE));
    params.set('offset', String((_rptJournalPage - 1) * _RPT_PER_PAGE));

    const data: RaportJurnal = await api(`/api/reports/maintenance?${params}`);
    const summaryEl = $('#reports-summary');
    const tbody = $('#reports-table tbody');
    if (!tbody) return;

    const total = data.total || 0;
    const countEl = document.getElementById('reports-count');
    if (countEl) {
        const shown = (data.entries || []).length;
        const first = total ? (_rptJournalPage - 1) * _RPT_PER_PAGE + 1 : 0;
        const parts = [shown < total ? `${first}–${first + shown - 1} din ${total}` : `${total} înregistrări`];
        if (!showSynthetic && data.synthetic_count) parts.push(`${data.synthetic_count} baseline GO-LIVE ascunse`);
        countEl.textContent = parts.join(' · ');
    }
    if (document.getElementById('reports-pagination')) {
        renderPagination('#reports-pagination', total, _RPT_PER_PAGE, _rptJournalPage, (p: number) => loadReportsData(p));
    }

    if (summaryEl) {
        let html = '<div class="reports-summary-cards">';
        html += `<div class="stat-card"><div class="label">${showSynthetic ? 'Total (cu baseline)' : 'Lucrări reale'}</div><div class="value">${data.total || 0}</div></div>`;
        if (data.by_machine && Object.keys(data.by_machine).length) {
            const topMachines = Object.entries(data.by_machine as Record<string, number>).sort((a, b) => b[1] - a[1]).slice(0, 5);
            topMachines.forEach(([mid, cnt]) => { html += `<div class="stat-card"><div class="label">${escHtml(mid)}</div><div class="value">${cnt}</div></div>`; });
        }
        if (data.by_category && Object.keys(data.by_category).length) {
            const topCats = Object.entries(data.by_category as Record<string, number>).sort((a, b) => b[1] - a[1]).slice(0, 5);
            topCats.forEach(([cat, cnt]) => { html += `<div class="stat-card"><div class="label">${escHtml(cat)}</div><div class="value">${cnt}</div></div>`; });
        }
        html += '</div>';
        summaryEl.innerHTML = html;
    }

    const entries = data.entries || [];
    tbody.innerHTML = '';
    if (entries.length === 0) {
        tbody.innerHTML = '<tr><td colspan="10" class="empty-state">Nicio înregistrare</td></tr>';
        return;
    }
    entries.forEach((entry) => {
        const vs = entry.validation_status || 'pending';
        const vsMapR = {
            pending:               { label: 'Așteaptă',      bg: '#f59e0b', color: '#000' },
            validated:             { label: 'Validat',        bg: '#22c55e', color: '#fff' },
            rejected:              { label: 'Respins',        bg: '#ef4444', color: '#fff' },
            needs_clarification:   { label: 'Clarificare!',   bg: '#f97316', color: '#000' },
            // 'auto' = închis fără validare umană: fie baseline GO-LIVE (sintetic),
            // fie backlog pending arhivat în bloc (politica Dan, 30 iul 2026).
            auto:                  { label: 'Auto (nevalidat)', bg: '#475569', color: '#e2e8f0' },
        };
        const vsCfg = (vsMapR as Record<string, { label: string; bg: string; color: string }>)[vs] || { label: vs, bg: 'gray', color: '#fff' };
        const vsTitle = vs === 'auto'
            ? (entry.synthetic ? 'Baseline GO-LIVE — nu este o lucrare reală' : (entry.validation_notes || 'Închis automat, fără validare umană'))
            : (entry.validation_notes || '');
        const vsBadge = `<span title="${escHtml(vsTitle)}" style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:.78rem;font-weight:700;background:${vsCfg.bg};color:${vsCfg.color};white-space:nowrap">${escHtml(vsCfg.label)}</span>`;
        const synthBadge = entry.synthetic
            ? ' <span title="Baseline GO-LIVE — mașina considerată la zi la pornire, nu o lucrare efectuată" style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:.7rem;font-weight:700;background:rgba(148,163,184,.18);color:#94a3b8">BASELINE</span>'
            : '';
        const tr = document.createElement('tr');
        tr.style.cursor = 'pointer';
        if (vs === 'needs_clarification') tr.style.background = 'rgba(249,115,22,.08)';
        if (entry.synthetic) tr.style.opacity = '.65';
        tr.innerHTML = `
            <td style="font-size:.8rem;font-weight:600;color:var(--accent);white-space:nowrap">${escHtml(entry.registration_number || '—')}</td>
            <td>${escHtml(formatDateRO(entry.date))}</td>
            <td><strong>${escHtml(entry.machine_id)}</strong>${synthBadge}</td>
            <td><span class="badge ${entry.tip === 'urgenta' ? 'badge-red' : entry.tip === 'revizie' ? 'badge-green' : entry.tip === 'ad_hoc' ? 'badge-gray' : 'badge-blue'}" title="${escHtml(entry.tip_eticheta || '')}">${escHtml(_TIP_SCURT[entry.tip] || entry.tip || '—')}</span></td>
            <td><span class="badge badge-blue">${escHtml(entry.category || '—')}</span></td>
            <td style="max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(truncate(entry.action, 60))}</td>
            <td>${escHtml(entry.technician)}</td>
            <td>${vsBadge}</td>
            <td>${entry.duration_minutes != null ? entry.duration_minutes + ' min' : '—'}</td>
            <td><button class="btn btn-sm log-view-btn" data-log-id="${entry.id}">👁 Deschide</button></td>
        `;
        tr.addEventListener('click', (e) => {
            if (!(e.target as HTMLElement).closest('.log-view-btn')) openLogDetail(entry.id);
        });
        tbody.appendChild(tr);
    });
    tbody.querySelectorAll('.log-view-btn').forEach(btn => {
        btn.addEventListener('click', () => openLogDetail(Number((btn as HTMLElement).dataset.logId)));
    });
}

export function _parseAiSteps(text: string) {
    if (!text) return [];
    const lines = text.split('\n');
    const steps = [];
    let currentStep = null;
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const stepMatch = trimmed.match(/^(\d+)\.\s*\**(.+)/);
        const dashMatch = trimmed.match(/^[-–]\s*\**(.+)/);
        const hashMatch = trimmed.match(/^#{1,4}\s+(.+)/);
        if (stepMatch) {
            if (currentStep) steps.push(currentStep);
            currentStep = { text: stepMatch[2].replace(/\*+/g, '').trim(), removed: false };
        } else if (hashMatch && !currentStep) {
            if (currentStep) steps.push(currentStep);
            currentStep = { text: hashMatch[1].replace(/\*+/g, '').trim(), removed: false, isHeader: true };
        } else if (dashMatch && currentStep && !currentStep.isHeader) {
            currentStep.text += ' → ' + dashMatch[1].replace(/\*+/g, '').trim();
        } else if (currentStep && !currentStep.isHeader) {
            currentStep.text += ' ' + trimmed.replace(/\*+/g, '').trim();
        } else {
            if (currentStep) steps.push(currentStep);
            currentStep = { text: trimmed.replace(/\*+/g, '').trim(), removed: false };
        }
    }
    if (currentStep) steps.push(currentStep);
    return steps.filter(s => s.text.length > 3);
}

export function _rebuildAiStepsText(steps: PasAi[]) {
    return steps
        .filter((s) => !s.removed)
        .map((s, i: number) => s.isHeader ? `### ${s.text}` : `${i + 1}. ${s.text}`)
        .join('\n');
}

export async function openLogDetail(logId: string|number) {
    const log: JurnalDetaliu | null = await api('/api/maintenance_log/' + logId).catch(() => null);
    if (!log) { toast('Nu s-au găsit datele', 'error'); return; }
    let existing = document.getElementById('log-detail-overlay');
    if (existing) existing.remove();

    let stepsJson = null;
    try { stepsJson = JSON.parse(log.notes!); } catch(e) {}   // notes gol: JSON.parse(null) dă null
    const isAiGuided = stepsJson && stepsJson.type === 'ai_guided' && Array.isArray(stepsJson.steps);
    const hasRec = !!log.recording_filename;
    const vs = log.validation_status || 'pending';
    const vsLabel = ({pending:'⏳ Așteaptă validare', validated:'✅ Validat', rejected:'❌ Respins', needs_clarification:'🔄 Trimis la clarificare'} as Record<string, string>)[vs] || vs;
    const vsColor = ({pending:'orange', validated:'green', rejected:'red', needs_clarification:'#f59e0b'} as Record<string, string>)[vs] || 'inherit';

    let stepsHtml = '';
    if (isAiGuided) {
        const activeSteps = stepsJson.steps.filter((s: PasSalvat) => !s.deleted);
        const completedSteps = activeSteps.filter((s: PasSalvat) => s.completed).length;
        stepsHtml += '<div style="margin:12px 0"><strong>Pași AI (' + completedSteps + '/' + activeSteps.length + ' completați):</strong></div>';
        stepsHtml += '<div id="log-val-steps" style="max-height:300px;overflow-y:auto">';
        stepsJson.steps.forEach((s: PasSalvat, i: number) => {
            const fb = s.feedback === 'good' ? ' 👍' : s.feedback === 'bad' ? ' 👎' : '';
            const del = s.deleted ? ' style="opacity:.3;text-decoration:line-through"' : '';
            const check = s.completed ? '✅' : '⬜';
            stepsHtml += '<div class="log-step-row" data-sidx="' + i + '"' + del + ' style="display:flex;align-items:flex-start;gap:8px;padding:8px 10px;margin:3px 0;background:' +
                (s.deleted ? 'rgba(239,68,68,.05)' : s.completed ? 'rgba(34,197,94,.06)' : 'var(--surface)') +
                ';border-radius:8px;border:1px solid var(--border)">' +
                '<span style="min-width:24px">' + check + '</span>' +
                '<span style="flex:1;font-size:.95rem">' + colleagueMd(s.text) + fb + '</span>' +
                (vs === 'pending' ? '<button class="btn btn-sm log-step-del" data-sidx="' + i + '" title="Elimină pas" style="font-size:.8rem;padding:2px 6px">❌</button>' : '') +
                '</div>';
        });
        stepsHtml += '</div>';
        if (stepsJson.engineer_notes) {
            stepsHtml += '<div style="margin-top:8px;padding:8px;background:rgba(59,130,246,.06);border-radius:6px;font-size:.9rem"><strong>Note inginer:</strong> ' + escHtml(stepsJson.engineer_notes) + '</div>';
        }
    }

    let validationSection = '';
    const isMasterOrEngineer = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');
    // 30 sep 2026 (INT-02235): o intervenție în lucru / în pauză nu se validează nici de aici —
    // validarea o închidea cu munca încă nefăcută (serverul o refuză acum cu 409)
    const inLucru = log.status === 'active' || log.status === 'paused';
    const canValidate = (vs === 'pending' || vs === 'needs_clarification') && isMasterOrEngineer && !inLucru;
    const isOperatorClarify = vs === 'needs_clarification' && !isMasterOrEngineer;
    let existingLessonHtml = '';
    let _valAiSteps: PasAi[] = [];
    if (canValidate) {
        try {
            const lessonRes: { data: RandDiagnosticCases[] } = await api('/api/diagnostic_cases?limit=200');
            const linkedLesson = (lessonRes.data || []).find((l) => l.source_type === 'intervention' && l.source_id === logId);
            if (linkedLesson && linkedLesson.ai_suggested_steps) {
                _valAiSteps = _parseAiSteps(linkedLesson.ai_suggested_steps);
                let stepsListHtml = '';
                _valAiSteps.forEach((step, i) => {
                    stepsListHtml += `<div class="val-ai-step" data-idx="${i}" style="display:flex;align-items:flex-start;gap:8px;padding:8px 10px;margin:3px 0;background:var(--surface);border-radius:8px;border:1px solid var(--border)">
                        <span style="min-width:28px;font-weight:700;color:var(--accent)">${i + 1}.</span>
                        <span class="val-ai-step-text" style="flex:1;font-size:.92rem;line-height:1.4">${colleagueMd(step.text)}</span>
                        <button class="btn btn-sm val-ai-step-del" data-idx="${i}" title="Elimină acest pas" style="font-size:.8rem;padding:2px 8px;min-width:30px;color:var(--red)">✕</button>
                    </div>`;
                });
                existingLessonHtml = `
                <div style="margin-bottom:12px;padding:12px;border-radius:8px;background:rgba(59,130,246,.06);border:1px solid rgba(59,130,246,.2)">
                    <label style="font-size:.95rem;font-weight:600">🤖 Pași sugerați de AI (click ✕ pentru a elimina un pas greșit):</label>
                    <div id="log-val-ai-steps-list" style="margin-top:8px;max-height:350px;overflow-y:auto">${stepsListHtml}</div>
                    <input type="hidden" id="log-val-lesson-id" value="${linkedLesson.id}">
                    <div style="font-size:.8rem;color:var(--text-muted);margin-top:6px">Pașii eliminați nu vor fi salvați în lecție.</div>
                </div>`;
            }
        } catch(e) {}

        existingLessonHtml += `
            <div style="margin-bottom:12px">
                <label style="font-size:.95rem;font-weight:600">📝 Corecții / observații sugestii AI (opțional):</label>
                <textarea id="log-val-ai-feedback" rows="3" placeholder="Ex: Sugestia AI de a verifica senzorul X a fost greșită, problema era la releu. Operatorul a urmat manualul PDF secțiunea 4.2..." style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px;resize:vertical;font-size:.9rem"></textarea>
                <div style="font-size:.8rem;color:var(--text-muted);margin-top:4px">Dacă operatorul a urmat altă procedură sau sugestiile AI au fost greșite, notează aici.</div>
            </div>`;
    }

    if (canValidate) {
        const clarifyNote = vs === 'needs_clarification' 
            ? `<div style="margin-bottom:12px;padding:10px;border-radius:8px;background:rgba(245,158,11,.1);border:1px solid rgba(245,158,11,.3)">
                <strong style="color:#f59e0b">🔄 Această intervenție a fost returnată la clarificare</strong>
                ${log.validation_notes ? '<br><span style="font-size:.9rem;color:var(--text-muted)">Motiv: ' + escHtml(log.validation_notes) + '</span>' : ''}
                ${log.validated_by ? '<br><span style="font-size:.85rem;color:var(--text-muted)">De: ' + escHtml(log.validated_by) + '</span>' : ''}
            </div>` : '';
        validationSection = `
        <div style="margin-top:16px;padding:16px;border:2px solid var(--border);border-radius:10px;background:rgba(59,130,246,.04)">
            <h4 style="margin:0 0 10px;color:var(--accent)">🔒 Validare inginer</h4>
            ${clarifyNote}
            <div style="margin-bottom:8px">
                <label style="font-size:.95rem;font-weight:600">Validează:</label>
                <input type="text" id="log-val-engineer" value="${escHtml(state.currentUser.username || '')}" readonly title="Se scrie contul cu care ești logat" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-muted);margin-top:4px">
            </div>
            <div style="margin-bottom:10px">
                <label style="font-size:.95rem;font-weight:600">Observații validare:</label>
                <textarea id="log-val-notes" rows="2" placeholder="Observații despre calitatea intervenției..." style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px"></textarea>
            </div>
            ${existingLessonHtml}
            <div style="display:flex;gap:8px;flex-wrap:wrap">
                <button class="btn btn-green" id="log-val-approve" style="flex:1;min-width:140px;padding:10px">✅ VALIDEAZĂ</button>
                <button class="btn" id="log-val-clarify" style="flex:1;min-width:140px;padding:10px;background:#f59e0b;color:#000;border:none;border-radius:8px;font-weight:600">🔄 CLARIFICARE</button>
                <button class="btn" id="log-val-reject" style="flex:1;min-width:140px;padding:10px;background:var(--red);color:#fff;border:none;border-radius:8px">❌ RESPINGE</button>
            </div>
        </div>`;
    } else if (isOperatorClarify) {
        validationSection = `
        <div style="margin-top:16px;padding:16px;border:2px solid #f59e0b;border-radius:10px;background:rgba(245,158,11,.06)">
            <h4 style="margin:0 0 8px;color:#f59e0b">🔄 Necesită clarificare / completare</h4>
            <p style="margin:0 0 8px;font-size:.95rem;color:var(--text-muted)">Inginerul a solicitat completarea acestei intervenții.</p>
            ${log.validation_notes ? '<div style="padding:8px;border-radius:6px;background:rgba(245,158,11,.08);margin-bottom:10px;font-size:.95rem"><strong>Motiv:</strong> ' + escHtml(log.validation_notes) + '</div>' : ''}
            ${log.validated_by ? '<div style="font-size:.85rem;color:var(--text-muted);margin-bottom:10px">Solicitat de: ' + escHtml(log.validated_by) + (log.validation_date ? ' la ' + escHtml(log.validation_date) : '') + '</div>' : ''}
            <div style="margin-bottom:8px">
                <label style="font-size:.95rem;font-weight:600">Completează acțiunile / observațiile:</label>
                <textarea id="log-clarify-response" rows="3" placeholder="Descrie ce s-a făcut, pașii urmați, observații..." style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-main);color:var(--text-main);margin-top:4px">${escHtml(log.action || '')}</textarea>
            </div>
            <button class="btn btn-green" id="log-clarify-submit" style="width:100%;padding:10px">📤 TRIMITE COMPLETAREA</button>
        </div>`;
    } else if (vs === 'pending' && !isMasterOrEngineer) {
        validationSection = `
        <div style="margin-top:16px;padding:14px;border:1px solid var(--border);border-radius:10px;background:rgba(255,170,0,.06);text-align:center">
            <span style="font-size:1.5rem">🔒</span>
            <p style="margin:6px 0 0;color:var(--text-muted);font-size:.95rem">Validarea este disponibilă doar pentru <strong>ingineri</strong>.<br>Autentifică-te ca <em>master</em> pentru a valida această intervenție.</p>
        </div>`;
    } else {
        const statusBg = vs === 'validated' ? 'rgba(34,197,94,.08)' : vs === 'needs_clarification' ? 'rgba(245,158,11,.08)' : 'rgba(239,68,68,.08)';
        validationSection = '<div style="margin-top:12px;padding:10px;border-radius:8px;background:' + statusBg + '">' +
            '<strong>Status:</strong> ' + vsLabel +
            (log.validated_by ? ' <em>de ' + escHtml(log.validated_by) + '</em>' : '') +
            (log.validation_date ? ' la ' + escHtml(log.validation_date) : '') +
            (log.validation_notes ? '<br><strong>Observații:</strong> ' + escHtml(log.validation_notes) : '') +
            '</div>';
    }

    const overlay = document.createElement('div');
    overlay.id = 'log-detail-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `
      <div class="proc-container" style="max-width:700px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
          <h2 class="proc-title">${isAiGuided ? '🔧 Intervenție urgentă cu ghidare AI' : 'Vizualizare intervenție'}</h2>
          <button class="proc-close" id="log-detail-close" title="Închide">&times;</button>
        </div>
        <div class="proc-meta">
          ${log.registration_number ? '<div class="proc-meta-row"><span class="proc-meta-label">Nr. înregistrare</span><span class="proc-meta-val" style="font-weight:700;color:var(--accent);font-size:1.1rem">' + escHtml(log.registration_number) + '</span></div>' : ''}
          <div class="proc-meta-row"><span class="proc-meta-label">Data</span><span class="proc-meta-val">${escHtml(log.date || '—')}</span></div>
          <div class="proc-meta-row"><span class="proc-meta-label">Mașină</span><span class="proc-meta-val">${escHtml(log.machine_id || '—')}</span></div>
          <div class="proc-meta-row"><span class="proc-meta-label">Status validare</span><span class="proc-meta-val" style="color:${vsColor}">${vsLabel}</span></div>
          ${log.technician ? '<div class="proc-meta-row"><span class="proc-meta-label">Operator</span><span class="proc-meta-val">' + escHtml(log.technician) + '</span></div>' : ''}
        </div></div>
        <div class="proc-body" style="padding:16px">
          <div style="margin-bottom:12px"><strong>Problema raportată:</strong>
            <div style="background:var(--surface);padding:10px;border-radius:8px;margin-top:4px;font-size:.95rem">${escHtml(log.action || '—')}</div>
          </div>
          ${stepsHtml}
          ${log.parts_used ? '<div style="margin:12px 0"><strong>Piese utilizate:</strong> ' + escHtml(log.parts_used) + '</div>' : ''}
          ${isMasterOrEngineer ? '<div id="log-detail-sugg" style="display:none;margin:12px 0;padding:8px 10px;background:rgba(0,180,255,.05);border:1px dashed rgba(0,180,255,.3);border-radius:8px;font-size:.83rem"></div>' : ''}
          ${hasRec ? '<div style="margin-top:12px"><strong>Înregistrare sesiune:</strong><div style="margin-top:6px"><video controls style="width:100%;max-height:250px;border-radius:8px" src="/api/maintenance_log/' + log.id + '/recording"></video></div></div>' : ''}
          ${validationSection}
        </div>
      </div>`;
    document.body.appendChild(overlay);
    document.getElementById('log-detail-close')!.addEventListener('click', () => overlay.remove());
    // legare la mentenanța planificată — și din istoric (cerința Dan 30 iul 2026)
    if (isMasterOrEngineer) {
        const suggEl = document.getElementById('log-detail-sugg');
        if (suggEl) incarcaSugestiiInterval(log.id, suggEl);
    }

    overlay.querySelectorAll('.log-step-del').forEach(btn => {
        btn.addEventListener('click', () => {
            const idx = parseInt((btn as HTMLElement).dataset.sidx!);
            const row = btn.closest('.log-step-row');
            if (row) { (row as HTMLElement).style.opacity = '0.2'; (row as HTMLElement).style.textDecoration = 'line-through'; }
            (btn as HTMLInputElement).disabled = true;
            btn.textContent = '🗑️';
            if (isAiGuided && stepsJson.steps[idx]) stepsJson.steps[idx].deleted = true;
        });
    });

    overlay.querySelectorAll('.val-ai-step-del').forEach(btn => {
        btn.addEventListener('click', function _toggle() {
            const idx = parseInt((btn as HTMLElement).dataset.idx!);
            if (!_valAiSteps[idx]) return;
            const row = btn.closest('.val-ai-step');
            const isRemoved = _valAiSteps[idx].removed;
            _valAiSteps[idx].removed = !isRemoved;
            if (row) {
                (row as HTMLElement).style.opacity = _valAiSteps[idx].removed ? '.25' : '1';
                (row.querySelector('.val-ai-step-text') as HTMLElement).style.textDecoration = _valAiSteps[idx].removed ? 'line-through' : 'none';
            }
            btn.textContent = _valAiSteps[idx].removed ? '↩' : '✕';
            (btn as HTMLElement).title = _valAiSteps[idx].removed ? 'Restaurează pasul' : 'Elimină acest pas';
            (btn as HTMLElement).style.color = _valAiSteps[idx].removed ? 'var(--accent)' : 'var(--red)';
        });
    });

    const approveBtn = document.getElementById('log-val-approve');
    const rejectBtn = document.getElementById('log-val-reject');
    if (approveBtn) {
        approveBtn.addEventListener('click', async () => {
            const eng = (document.getElementById('log-val-engineer') as HTMLInputElement)?.value?.trim();
            if (!eng) { toast('Scrie numele inginerului!', 'warning'); document.getElementById('log-val-engineer')?.focus(); return; }
            const notes = (document.getElementById('log-val-notes') as HTMLInputElement)?.value?.trim() || '';
            const lessonIdField = document.getElementById('log-val-lesson-id');
            const aiFeedback = (document.getElementById('log-val-ai-feedback') as HTMLInputElement)?.value?.trim() || '';
            try {
                await api('/api/maintenance_log/' + logId + '/validate', {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({
                        status:'validated', validated_by: eng, validation_notes: notes,
                        updated_steps: isAiGuided ? stepsJson.steps : null,
                        ai_feedback: aiFeedback || null
                    })
                });
                if (lessonIdField && (lessonIdField as HTMLInputElement).value && _valAiSteps.length > 0) {
                    try {
                        let correctedText = _rebuildAiStepsText(_valAiSteps);
                        if (aiFeedback) {
                            correctedText += (correctedText ? '\n\n' : '') + '--- CORECȚIE INGINER ---\n' + aiFeedback;
                        }
                        await api('/api/diagnostic_cases/' + (lessonIdField as HTMLInputElement).value, {
                            method:'PUT', headers:{'Content-Type':'application/json'},
                            body: JSON.stringify({ ai_suggested_steps: correctedText })
                        });
                    } catch(e2) { /* best-effort */ }
                }
                toast('✅ Intervenție validată cu succes!', 'success');
                overlay.remove();
                loadReportsData();
            } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
    }
    if (rejectBtn) {
        rejectBtn.addEventListener('click', async () => {
            const eng = (document.getElementById('log-val-engineer') as HTMLInputElement)?.value?.trim();
            if (!eng) { toast('Scrie numele inginerului!', 'warning'); document.getElementById('log-val-engineer')?.focus(); return; }
            const notes = (document.getElementById('log-val-notes') as HTMLInputElement)?.value?.trim();
            if (!notes) { toast('Adaugă observații despre ce este incorect!', 'warning'); document.getElementById('log-val-notes')?.focus(); return; }
            try {
                await api('/api/maintenance_log/' + logId + '/validate', {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({ status:'rejected', validated_by: eng, validation_notes: notes, updated_steps: isAiGuided ? stepsJson.steps : null })
                });
                toast('❌ Intervenție respinsă. Feedback-ul a fost salvat.', 'info');
                overlay.remove();
                loadReportsData();
            } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
    }

    const clarifyBtn = document.getElementById('log-val-clarify');
    if (clarifyBtn) {
        clarifyBtn.addEventListener('click', async () => {
            const eng = (document.getElementById('log-val-engineer') as HTMLInputElement)?.value?.trim();
            if (!eng) { toast('Scrie numele inginerului!', 'warning'); document.getElementById('log-val-engineer')?.focus(); return; }
            const notes = (document.getElementById('log-val-notes') as HTMLInputElement)?.value?.trim();
            if (!notes) { toast('Descrie ce trebuie clarificat/completat!', 'warning'); document.getElementById('log-val-notes')?.focus(); return; }
            try {
                await api('/api/maintenance_log/' + logId + '/validate', {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({ status:'needs_clarification', validated_by: eng, validation_notes: notes })
                });
                toast('🔄 Intervenția a fost trimisă la clarificare către operator.', 'info');
                overlay.remove();
                loadReportsData();
            } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
    }

    const clarifySubmitBtn = document.getElementById('log-clarify-submit');
    if (clarifySubmitBtn) {
        clarifySubmitBtn.addEventListener('click', async () => {
            const response = (document.getElementById('log-clarify-response') as HTMLInputElement)?.value?.trim();
            if (!response || response.length < 5) {
                toast('Completează descrierea acțiunilor (minim 5 caractere)!', 'warning');
                document.getElementById('log-clarify-response')?.focus();
                return;
            }
            try {
                await api('/api/maintenance_log/' + logId, {
                    method:'PUT', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({
                        action: response,
                        validation_status: 'pending',
                        validation_notes: ''
                    })
                });
                toast('📤 Completarea a fost trimisă. Intervenția revine la validare.', 'success');
                overlay.remove();
                if (typeof loadReportsData === 'function') loadReportsData();
                if (typeof loadJournalLog === 'function') loadJournalLog();
            } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
    }
}

const _WF_PER_PAGE = 50;
let _rptWfPage = 1;
let _rptWfFilterKey = '';

function _wfFilterParams(): URLSearchParams {
    const statusSel = document.getElementById('wf-reports-status') as HTMLSelectElement | null;
    const wfMachineSel = document.getElementById('wf-reports-machine') as HTMLSelectElement | null;
    const showCancelled = (document.getElementById('wf-reports-show-cancelled') as HTMLInputElement | null)?.checked === true;
    const wfLineSel = document.getElementById('wf-reports-line') as HTMLSelectElement | null;
    const params = new URLSearchParams();
    if (statusSel && statusSel.value) params.set('status', statusSel.value);
    if (wfMachineSel && wfMachineSel.value) params.set('machine_id', wfMachineSel.value);
    else if (wfLineSel && wfLineSel.value) params.set('line_id', wfLineSel.value);
    // Anulatele (352 fantome curățate pe 3 aug) înecau lista — ascunse dacă nu
    // sunt cerute explicit, fie prin bifă, fie prin filtrul de status.
    if (!showCancelled && statusSel?.value !== 'cancelled') params.set('exclude_status', 'cancelled');
    // Doar sesiuni reale de lucru — propunerile din briefingul Hub-ului au tabul lor.
    params.set('source', 'work');
    return params;
}

export async function _loadWorkflowReports(page?: number) {
    const params = _wfFilterParams();
    const filterKey = params.toString();
    if (filterKey !== _rptWfFilterKey) {
        _rptWfFilterKey = filterKey;
        _rptWfPage = 1;
    }
    if (page && page >= 1) _rptWfPage = page;
    params.set('limit', String(_WF_PER_PAGE));
    params.set('offset', String((_rptWfPage - 1) * _WF_PER_PAGE));
    params.set('meta', '1');
    const resp: ListaFluxuri | null = await api('/api/maintenance_workflows?' + params).catch(() => null);
    const wfs = Array.isArray(resp) ? resp : (resp?.items || []);
    const total = Array.isArray(resp) ? resp.length : (resp?.total ?? wfs.length);
    const tbody = document.querySelector('#wf-reports-table tbody');
    if (!tbody) return;
    const wfCountEl = document.getElementById('wf-reports-count');
    if (wfCountEl) {
        const first = total ? (_rptWfPage - 1) * _WF_PER_PAGE + 1 : 0;
        wfCountEl.textContent = wfs.length < total
            ? `${first}–${first + wfs.length - 1} din ${total}`
            : `${total} workflow-uri`;
    }
    if (document.getElementById('wf-reports-pagination')) {
        renderPagination('#wf-reports-pagination', total, _WF_PER_PAGE, _rptWfPage, (p: number) => _loadWorkflowReports(p));
    }
    if (!wfs || wfs.length === 0) {
        tbody.innerHTML = '<tr><td colspan="8" class="empty-state">Niciun workflow pentru filtrele selectate</td></tr>';
        return;
    }
    const statusLabels = { completed: '✅ Finalizat', paused: '⏸ În pauză', in_progress: '🔄 În desfășurare', pending: '⏳ Pregătire', validated: '✅ Validat', rejected: '❌ Respins', redo_scheduled: '🔁 De refăcut', cancelled: '🚫 Anulat' };
    const statusColors = { completed: 'green', paused: 'orange', in_progress: 'blue', pending: 'text-muted', validated: 'green', rejected: 'red', redo_scheduled: 'orange', cancelled: 'text-muted' };
    tbody.innerHTML = '';
    wfs.forEach((wf) => {
        const tr = document.createElement('tr');
        const dt = wf.started_at ? new Date(wf.started_at + (wf.started_at.includes('Z') ? '' : 'Z')) : null;
        const dateStr = dt ? dt.toLocaleDateString('ro-RO', {day:'2-digit',month:'2-digit',year:'numeric'}) + ' ' + dt.toLocaleTimeString('ro-RO', {hour:'2-digit',minute:'2-digit'}) : '—';
        const progress = wf.steps_total ? Math.round((wf.steps_completed || 0) / wf.steps_total * 100) : 0;
        const progressBar = wf.steps_total ? '<div class="wf-rpt-progress"><div class="wf-rpt-bar" style="width:' + progress + '%"></div><span>' + (wf.steps_completed || 0) + '/' + wf.steps_total + '</span></div>' : '—';
        const dur = wf.duration_minutes ? wf.duration_minutes + ' min' : '—';
        const st = (statusLabels as Record<string, string>)[wf.status!] || wf.status;
        tr.innerHTML = '<td>' + dateStr + '</td>'
            + '<td><strong>' + escHtml(wf.machine_name || wf.machine_id || '—') + '</strong></td>'
            + '<td>' + escHtml(truncate(wf.action_ro || '—', 50)) + '</td>'
            + '<td>' + escHtml(wf.operator_name || '—') + '</td>'
            + '<td>' + progressBar + '</td>'
            + '<td>' + dur + '</td>'
            + '<td><span style="color:var(--' + ((statusColors as Record<string, string>)[wf.status!] || 'text-muted') + ')">' + st + '</span></td>'
            + '<td><button class="btn btn-sm wf-view-detail-btn" data-wf-id="' + wf.id + '">🔍 Vezi</button></td>';
        tbody.appendChild(tr);
    });
    tbody.querySelectorAll('.wf-view-detail-btn').forEach(btn => {
        btn.addEventListener('click', () => openWorkflowDetail((btn as HTMLElement).dataset.wfId!));
    });
}


export async function openWorkflowDetail(wfId: string) {
    const [wf, enrichments]: [FluxDetaliu | null, CompletareFlux[]] = await Promise.all([
        api('/api/maintenance_workflows/' + wfId).catch(() => null),
        fetch('/api/work/workflow/' + wfId + '/enrichments').then(r => r.ok ? r.json() : []).catch(() => []),
    ]);
    if (!wf) { toast('Nu s-au găsit datele', 'error'); return; }
    let existing = document.getElementById('wf-detail-overlay');
    if (existing) existing.remove();
    const overlay = document.createElement('div');
    overlay.id = 'wf-detail-overlay';
    overlay.className = 'proc-overlay';
    const statusLabels = { completed: '✅ Finalizat', paused: '⏸ În pauză', in_progress: '🔄 În desfășurare', validated: '✅ Validat', rejected: '❌ Respins' };
    const dt = formatDateTimeRO(wf.started_at);
    const dtEnd = formatDateTimeRO(wf.completed_at);
    let stepsHtml = '';
    try {
        const stepsData: PasFlux[] = JSON.parse(wf.steps_data || '[]');
        if (stepsData.length > 0) {
            stepsHtml = '<div class="proc-steps-list" style="max-height:250px;overflow-y:auto">';
            stepsData.forEach((s, idx: number) => {
                const doneClass = s.done ? ' proc-step-done' : '';
                const icon = s.done ? '✅' : '⬜';
                let detailLines = [];
                detailLines.push(s.done ? 'Status: <strong style="color:var(--green)">Completat</strong>' : 'Status: <em>Necompletat</em>');
                if (s.completed_by) detailLines.push('Executat de: <strong>' + escHtml(s.completed_by) + '</strong>');
                if (s.completed_at) detailLines.push('Finalizat: ' + formatDateTimeRO(s.completed_at));
                if (s.duration_seconds != null && s.duration_seconds > 0) {
                    const dm = Math.floor(s.duration_seconds / 60);
                    const ds = s.duration_seconds % 60;
                    detailLines.push('Durată: ' + (dm > 0 ? dm + ' min ' : '') + ds + ' sec');
                }
                if (s.notes) detailLines.push('Notă: ' + escHtml(s.notes));
                const detailHtml = '<div class="wf-step-detail" style="display:none;padding:6px 12px 8px 36px;font-size:.82rem;color:var(--text-muted);border-top:1px solid rgba(255,255,255,.06)">' + detailLines.join(' &bull; ') + '</div>';
                stepsHtml += '<div class="proc-step' + doneClass + ' wf-step-inspectable" style="cursor:pointer" data-idx="' + idx + '">'
                    + '<label class="proc-step-label" style="pointer-events:none"><span class="proc-step-num">' + icon + '</span>'
                    + '<span class="proc-step-text">' + escHtml(s.text || 'Pas ' + s.step) + '</span>'
                    + '<span style="margin-left:auto;font-size:.7rem;opacity:.5">▼</span>'
                    + '</label>' + detailHtml + '</div>';
            });
            stepsHtml += '</div>';
        }
    } catch(e) {}
    if (!stepsHtml) stepsHtml = '<p style="color:var(--text-muted);padding:16px">Nu sunt date detaliate ale pașilor.</p>';
    // coloanele pot fi goale: `null > 0` e fals, iar `null / n` dă 0 — procentul iese 0, cum trebuie
    const pct = wf.steps_total! > 0 ? Math.round(wf.steps_completed! / wf.steps_total! * 100) : 0;
    const media = wf.media || [];
    let mediaHtml = '';
    if (media.length > 0) {
        mediaHtml = '<h4 style="margin-top:16px">Fișiere atașate</h4><div class="wf-media-list">';
        media.forEach((m) => {
            const chk = m.included ? 'checked' : '';
            if (m.media_type === 'video') {
                mediaHtml += `<div class="wf-media-item"><video src="/uploads/${escHtml(m.file_path)}" controls style="max-width:280px;max-height:200px;border-radius:6px"></video>`;
            } else {
                mediaHtml += `<div class="wf-media-item"><img src="/uploads/${escHtml(m.file_path)}" style="max-width:280px;max-height:200px;border-radius:6px;cursor:pointer" onclick="window.open('/uploads/${escHtml(m.file_path)}','_blank')">`;
            }
            mediaHtml += `<div style="font-size:.75rem;margin-top:4px">${escHtml(m.description || '')} <label style="display:block;margin-top:4px"><input type="checkbox" class="wf-media-incl" data-mid="${m.id}" ${chk}> Include în baza de date</label></div></div>`;
        });
        mediaHtml += '</div>';
    }
    const isReviewer = state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');
    let enrichmentsHtml = '';
    if (Array.isArray(enrichments) && enrichments.length > 0) {
        enrichmentsHtml = '<h4 style="margin-top:16px">Completări propuse</h4><div class="wf-enrichments-list">';
        enrichments.forEach(function(item) {
            const st = item.status === 'approved' ? '✅ Aprobat' : item.status === 'rejected' ? '❌ Respins' : '⏳ Așteaptă validare';
            enrichmentsHtml += '<div class="wf-enrichment-item">';
            enrichmentsHtml += '<div class="wf-enrichment-item-header"><span>' + escHtml(item.content_type || 'notă') + '</span><span>' + st + '</span></div>';
            if (item.text_content) enrichmentsHtml += '<div class="wf-enrichment-text">' + escHtml(item.text_content) + '</div>';
            if (item.file_url) {
                if (item.content_type === 'video') enrichmentsHtml += '<video src="' + escHtml(item.file_url) + '" controls class="wf-enrichment-media"></video>';
                else enrichmentsHtml += '<img src="' + escHtml(item.file_url) + '" class="wf-enrichment-media" alt="foto">';
            }
            if (isReviewer && item.status === 'pending') {
                enrichmentsHtml += '<div class="wf-enrichment-actions"><button class="btn btn-sm" style="background:var(--green);color:#fff" data-enr-approve="' + item.id + '">Validează</button>';
                enrichmentsHtml += '<button class="btn btn-sm" style="background:var(--red);color:#fff;margin-left:6px" data-enr-reject="' + item.id + '">Respinge</button></div>';
            }
            enrichmentsHtml += '</div>';
        });
        enrichmentsHtml += '</div>';
    }
    // doar o lucrare FINALIZATĂ primește verdict (una în pauză nu e terminată — 30 sep 2026)
    const canValidate = wf.status === 'completed' && !wf.validation_status &&
        state.currentUser && (state.currentUser.role === 'master' || state.currentUser.role === 'engineer');
    let validationHtml = '';
    if (wf.validation_status) {
        const vLabel = wf.validation_status === 'approved' ? '✅ Validat' : '❌ Respins';
        let redoBtn = '';
        if (wf.status === 'rejected') {
            redoBtn = `<div style="margin-top:12px;padding:12px;background:var(--surface);border-radius:8px;border:1px solid var(--border)">
                <h4 style="margin-bottom:6px">Reprogramare pentru refacere</h4>
                <div style="margin-bottom:6px"><label style="font-size:.85rem">Observații pentru operator:</label><textarea id="wf-redo-obs" style="width:100%;padding:6px;border-radius:6px;border:1px solid var(--border);background:var(--bg-card);color:var(--text-primary);min-height:50px;margin-top:4px" placeholder="Ce trebuie refăcut și de ce..."></textarea></div>
                <div style="margin-bottom:8px"><label style="font-size:.85rem">Scadență refacere: <input type="date" id="wf-redo-date" style="font-size:.85rem;margin-left:6px"></label></div>
                <button class="btn" style="background:var(--accent);color:#fff" id="wf-redo-btn">🔄 Reprogramează mentenanța</button>
            </div>`;
        }
        validationHtml = `<div class="wf-validation-result" style="padding:12px;border-radius:8px;margin-top:16px;background:${wf.validation_status === 'approved' ? 'rgba(34,197,94,.1)' : 'rgba(239,68,68,.1)'}"><strong>${vLabel}</strong> de ${escHtml(wf.validated_by || '—')} la ${wf.validated_at || '—'}${wf.validation_comment ? '<br>Comentariu: ' + escHtml(wf.validation_comment) : ''}</div>${redoBtn}`;
    } else if (canValidate) {
        validationHtml = `<div class="wf-validate-section" style="margin-top:16px;padding:16px;background:var(--surface);border-radius:8px;border:1px solid var(--border)">
            <h4 style="margin-bottom:8px">Validare inginer mentenanță</h4>
            <div style="margin-bottom:8px"><label style="font-size:.85rem">Comentariu:</label><textarea id="wf-val-comment" style="width:100%;padding:8px;border-radius:6px;border:1px solid var(--border);background:var(--bg-card);color:var(--text-primary);min-height:60px;margin-top:4px" placeholder="Observații, recomandări..."></textarea></div>
            <div style="margin-bottom:8px"><label style="font-size:.85rem">Încarcă foto/video:</label><input type="file" id="wf-val-upload" accept=".jpg,.jpeg,.png,.mp4,.webm,.mov" multiple style="margin-top:4px;font-size:.85rem"></div>
            <div style="display:flex;gap:10px;justify-content:flex-end">
                <button class="btn" style="background:var(--red);color:#fff" id="wf-val-reject">❌ Respinge</button>
                <button class="btn" style="background:var(--green);color:#fff" id="wf-val-approve">✅ Validează și salvează în jurnal</button>
            </div>
        </div>`;
    }
    overlay.innerHTML = `
        <div class="proc-container" style="max-width:720px;overflow-y:auto">
            <div class="proc-header"><div class="proc-header-top">
                <h2 class="proc-title">Verificare: ${escHtml(wf.action_ro || 'Mentenanță')}</h2>
                <button class="proc-close" title="Închide">&times;</button>
            </div>
            <div class="proc-meta">
                <div class="proc-meta-row"><span class="proc-meta-label">Mașină</span><span class="proc-meta-val">${escHtml(wf.machine_name || wf.machine_id || '—')}</span></div>
                <div class="proc-meta-row"><span class="proc-meta-label">Operator</span><span class="proc-meta-val">${escHtml(wf.operator_name || '—')} (${escHtml(wf.operator_level || '—')})</span></div>
                <div class="proc-meta-row"><span class="proc-meta-label">Început</span><span class="proc-meta-val">${dt}</span></div>
                <div class="proc-meta-row"><span class="proc-meta-label">Finalizat</span><span class="proc-meta-val">${dtEnd}</span></div>
                <div class="proc-meta-row"><span class="proc-meta-label">Durată</span><span class="proc-meta-val">${wf.duration_minutes ? wf.duration_minutes + ' min' : '—'}</span></div>
                <div class="proc-meta-row"><span class="proc-meta-label">Status</span><span class="proc-meta-val">${(statusLabels as Record<string, string>)[wf.status!] || wf.status}</span></div>
            </div></div>
            <div class="proc-body" style="padding:16px">
                <h3 style="margin-bottom:8px">Progres: ${wf.steps_completed || 0} / ${wf.steps_total || 0} (${pct}%)</h3>
                <div class="wf-exec-progress" style="margin-bottom:12px"><div class="wf-exec-progress-bar" style="width:${pct}%"></div></div>
                ${stepsHtml}
                ${wf.notes ? _renderStructuredNotes(wf.notes) : ''}
                ${mediaHtml}
                ${enrichmentsHtml}
                ${validationHtml}
            </div>
        </div>`;
    document.body.appendChild(overlay);
    overlay.querySelector('.proc-close')!.addEventListener('click', () => overlay.remove());

    overlay.querySelectorAll('.wf-step-inspectable').forEach(el => {
        el.addEventListener('click', () => {
            const det = el.querySelector('.wf-step-detail');
            if (det) (det as HTMLElement).style.display = (det as HTMLElement).style.display === 'none' ? 'block' : 'none';
        });
    });

    overlay.querySelectorAll('.wf-media-incl').forEach(chk => {
        chk.addEventListener('change', async () => {
            await fetch('/api/workflow_media/' + (chk as HTMLElement).dataset.mid + '/include', {
                method: 'POST', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ include: (chk as HTMLInputElement).checked })
            }).catch(() => {});
        });
    });

    overlay.querySelectorAll('[data-enr-approve]').forEach(btn => {
        btn.addEventListener('click', async () => {
            const id = parseInt(btn.getAttribute('data-enr-approve') || '0', 10);
            const raspuns = await askText('Aprobare completare', { label: 'Comentariu validare (opțional):', multiline: true, okText: 'Aprobă' });
            if (raspuns === null) return;
            const notes = raspuns;
            try {
                const res = await fetch('/api/work/enrichments/' + id + '/review', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({ status: 'approved', review_notes: notes.trim() || null }),
                });
                const data = await res.json();
                if (data.ok) { toast('Completare aprobată', 'success'); openWorkflowDetail(wfId); }
                else toast(data.error || 'Eroare', 'error');
            } catch (e) { toast('Eroare rețea', 'error'); }
        });
    });
    overlay.querySelectorAll('[data-enr-reject]').forEach(btn => {
        btn.addEventListener('click', async () => {
            const id = parseInt(btn.getAttribute('data-enr-reject') || '0', 10);
            const raspuns = await askText('Respingere completare', { label: 'Motiv respingere (obligatoriu):', multiline: true, required: true, okText: 'Respinge' });
            if (raspuns === null) return;
            const notes = raspuns;
            if (!notes.trim()) { toast('Motiv obligatoriu', 'warning'); return; }
            try {
                const res = await fetch('/api/work/enrichments/' + id + '/review', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({ status: 'rejected', review_notes: notes.trim() }),
                });
                const data = await res.json();
                if (data.ok) { toast('Completare respinsă', 'info'); openWorkflowDetail(wfId); }
                else toast(data.error || 'Eroare', 'error');
            } catch (e) { toast('Eroare rețea', 'error'); }
        });
    });

    const uploadInput = overlay.querySelector('#wf-val-upload');
    if (uploadInput) {
        uploadInput.addEventListener('change', async () => {
            for (const file of (uploadInput as HTMLInputElement).files!) {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('uploaded_by', 'inginer');
                try {
                    const res = await fetch('/api/maintenance_workflows/' + wfId + '/media', { method: 'POST', body: fd });
                    if (res.ok) toast('Fișier încărcat: ' + file.name, 'success');
                } catch(e) {}
            }
            openWorkflowDetail(wfId);
        });
    }

    const approveBtn = overlay.querySelector('#wf-val-approve');
    const rejectBtn = overlay.querySelector('#wf-val-reject');
    async function doValidate(vStatus: string) {
        const comment = ((overlay.querySelector('#wf-val-comment') || {}) as { value?: string }).value || '';
        if (vStatus === 'rejected' && !comment.trim()) {
            const ta = overlay.querySelector('#wf-val-comment');
            if (ta) { (ta as HTMLElement).style.border = '2px solid var(--red)'; (ta as HTMLElement).focus(); }
            toast('Adaugă un comentariu la respingere!', 'warning');
            return;
        }
        if (vStatus === 'approved' && !await customConfirm('Intervenția va fi salvată în jurnalul de mentenanță.', '✅ Confirmi validarea?')) return;
        try {
            const res = await fetch('/api/maintenance_workflows/' + wfId + '/validate', {
                method: 'POST', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({ validation_status: vStatus, comment: comment, validated_by: state.currentUser?.username || '' })
            });
            const data = await res.json();
            if (res.ok) {
                toast(vStatus === 'approved' ? 'Intervenție validată și salvată în jurnal!' : 'Intervenție respinsă.', vStatus === 'approved' ? 'success' : 'warning');
                overlay.remove();
                _loadWorkflowReports();
            } else { toast(data.error || 'Eroare', 'error'); }
        } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
    }
    if (approveBtn) approveBtn.addEventListener('click', () => doValidate('approved'));
    if (rejectBtn) rejectBtn.addEventListener('click', () => doValidate('rejected'));

    const redoBtn = overlay.querySelector('#wf-redo-btn');
    if (redoBtn) {
        redoBtn.addEventListener('click', async () => {
            const obs = ((overlay.querySelector('#wf-redo-obs') || {}) as { value?: string }).value || '';
            const dueDate = ((overlay.querySelector('#wf-redo-date') || {}) as { value?: string }).value || '';
            if (!obs.trim()) { toast('Scrie observații pentru operator', 'warning'); (overlay.querySelector('#wf-redo-obs') as HTMLElement)?.focus(); return; }
            if (!dueDate) { toast('Setează o dată scadentă', 'warning'); (overlay.querySelector('#wf-redo-date') as HTMLElement)?.focus(); return; }
            try {
                const res = await fetch('/api/maintenance_workflows/' + wfId + '/redo', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({ observations: obs, due_date: dueDate })
                });
                const d = await res.json();
                if (res.ok) {
                    overlay.remove();
                    toast('Refacerea e programată pe ' + formatDateRO(d.due_date || dueDate)
                        + (d.assigned_to ? ' — apare în „Lucrările mele" la ' + d.assigned_to : ' — nealocată, o găsești în Calendar'), 'success');
                    _loadWorkflowReports();
                } else { toast(d.error || 'Eroare', 'error'); }
            } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
        });
    }
}

/* ═══ PAGINATION HELPER ════════════════════════════════════════ */
export function renderPagination(selector: string, total: number, perPage: number, currentPage: number, onClick: (page: number) => void) {
    const el = $(selector);
    const totalPages = Math.ceil(total / perPage) || 1;
    el!.innerHTML = '';

    if (totalPages <= 1) return;

    const prev = document.createElement('button');
    prev.textContent = '← Prev';
    prev.disabled = currentPage <= 1;
    prev.addEventListener('click', () => onClick(currentPage - 1));
    el!.appendChild(prev);

    const maxShow = 7;
    let start = Math.max(1, currentPage - 3);
    let end = Math.min(totalPages, start + maxShow - 1);
    if (end - start < maxShow - 1) start = Math.max(1, end - maxShow + 1);

    for (let i = start; i <= end; i++) {
        const btn = document.createElement('button');
        btn.textContent = String(i);
        if (i === currentPage) btn.classList.add('active');
        btn.addEventListener('click', () => onClick(i));
        el!.appendChild(btn);
    }

    const next = document.createElement('button');
    next.textContent = 'Next →';
    next.disabled = currentPage >= totalPages;
    next.addEventListener('click', () => onClick(currentPage + 1));
    el!.appendChild(next);
}

/* ═══ 3D MODEL UPLOAD ════════════════════════════════════════ */

export async function _show3DUploadDialog() {
    let models: RandModels3d[] = [];
    try { models = await api('/api/3d-models'); } catch(e) {}

    let structure: FactoryStructure | null = null;
    try { structure = await api('/api/factory_structure'); } catch (e) {}
    const prodLines = (structure && structure.production_lines) ? structure.production_lines : [];
    const lineOpts = prodLines.map((pl: { id: string|null|undefined; product: string|null|undefined; }) => `<option value="${escHtml(pl.id)}">${escHtml(pl.id)}${pl.product ? ' — ' + escHtml(pl.product) : ''}</option>`).join('');

    const machines = state.allEquipmentIds || [];
    const machineOpts = machines.map(id => `<option value="${id}">${id}</option>`).join('');

    const modelRows = models.map((m) => {
        const statusBadge = m.conversion_status === 'done'
            ? '<span style="color:#22c55e">&#10003; GLB</span>'
            : m.conversion_status === 'error'
            ? `<span style="color:#ef4444" title="${(m.conversion_error||'').substring(0,100)}">&#10007; Eroare</span>`
            : '<span style="color:#eab308">&#9679; Conversie...</span>';
        const sizeInfo = m.file_size_mb ? `${m.file_size_mb} MB` : '';
        const typeLabel = m.model_type === 'hall' ? 'Hală' : m.model_type === 'line' ? 'Linie' : 'Mașină';
        const assoc = m.model_type === 'line' ? (m.line_id || '—') : (m.model_type === 'hall' ? '—' : (m.machine_id || '—'));
        return `<tr>
            <td>${escHtml(m.name)}</td>
            <td>${typeLabel}</td>
            <td>${escHtml(String(assoc))}</td>
            <td>${statusBadge} ${sizeInfo}</td>
            <td>
                <button class="btn btn-sm" onclick="_delete3DModel(${m.id})" style="color:#ef4444;font-size:.7rem" title="Șterge">✕</button>
            </td>
        </tr>`;
    }).join('');

    const html = `
        <div class="overlay-backdrop" id="overlay-3d-upload" style="position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center">
            <div style="background:var(--card-bg);border:1px solid var(--border);border-radius:12px;padding:1.5rem;max-width:650px;width:95%;max-height:85vh;max-height:85dvh;overflow-y:auto">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem">
                    <h3 style="margin:0;color:var(--cyan)">🧊 Modele 3D - Digital Twin</h3>
                    <button onclick="document.getElementById('overlay-3d-upload')?.remove()" style="background:none;border:none;color:var(--text-muted);font-size:1.2rem;cursor:pointer">✕</button>
                </div>

                <form id="form-3d-upload" style="display:flex;flex-direction:column;gap:.6rem;margin-bottom:1.2rem;padding:1rem;background:rgba(139,92,246,.08);border:1px solid rgba(139,92,246,.2);border-radius:8px">
                    <div style="font-size:.8rem;font-weight:700;color:#a78bfa;margin-bottom:.3rem">Încarcă model STEP / IGES</div>
                    <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem">
                        <div>
                            <label style="font-size:.7rem;color:var(--text-muted)">Fișier (.stp, .step, .igs, .iges)</label>
                            <input type="file" name="file" accept=".stp,.step,.igs,.iges" required style="font-size:.8rem;width:100%">
                        </div>
                        <div>
                            <label style="font-size:.7rem;color:var(--text-muted)">Nume</label>
                            <input type="text" name="name" placeholder="ex: Layout fabrică" style="width:100%;padding:.3rem .5rem;background:var(--bg);border:1px solid var(--border);border-radius:4px;color:var(--text);font-size:.8rem">
                        </div>
                        <div>
                            <label style="font-size:.7rem;color:var(--text-muted)">Tip model</label>
                            <select name="model_type" style="width:100%;padding:.3rem .5rem;background:var(--bg);border:1px solid var(--border);border-radius:4px;color:var(--text);font-size:.8rem">
                                <option value="hall">Hală / layout întreagă (un singur fișier)</option>
                                <option value="line">Linie producție (un STEP/GLB per linie)</option>
                                <option value="machine">Mașină (înlocuiește silueta pe hartă)</option>
                            </select>
                        </div>
                        <div id="wrap-3d-line-id" style="display:none">
                            <label style="font-size:.7rem;color:var(--text-muted)">Linie (din structura fabricii)</label>
                            <select name="line_id" style="width:100%;padding:.3rem .5rem;background:var(--bg);border:1px solid var(--border);border-radius:4px;color:var(--text);font-size:.8rem">
                                <option value="">— Alege linia —</option>
                                ${lineOpts || '<option value="" disabled>Nicio linie în DB</option>'}
                            </select>
                        </div>
                        <div id="wrap-3d-machine-id">
                            <label style="font-size:.7rem;color:var(--text-muted)">Mașina asociată</label>
                            <select name="machine_id" style="width:100%;padding:.3rem .5rem;background:var(--bg);border:1px solid var(--border);border-radius:4px;color:var(--text);font-size:.8rem">
                                <option value="">— Niciuna —</option>
                                ${machineOpts}
                            </select>
                        </div>
                    </div>
                    <div style="display:grid;grid-template-columns:1fr 1fr;gap:.5rem">
                        <div>
                            <label style="font-size:.7rem;color:var(--text-muted)">Toleranță mesh (mai mic = mai detaliat, fișier mai mare)</label>
                            <input type="number" name="tolerance" value="0.5" min="0.01" max="5" step="0.1" style="width:100%;padding:.3rem .5rem;background:var(--bg);border:1px solid var(--border);border-radius:4px;color:var(--text);font-size:.8rem">
                        </div>
                        <div>
                            <label style="font-size:.7rem;color:var(--text-muted)">Toleranță unghiulară</label>
                            <input type="number" name="angular_tolerance" value="0.3" min="0.01" max="2" step="0.1" style="width:100%;padding:.3rem .5rem;background:var(--bg);border:1px solid var(--border);border-radius:4px;color:var(--text);font-size:.8rem">
                        </div>
                    </div>
                    <button type="submit" class="btn btn-green" id="btn-3d-submit" style="align-self:flex-start;padding:.4rem 1.2rem">Încarcă și convertește → GLB</button>
                    <div id="upload-3d-status" style="font-size:.75rem;color:var(--text-muted);display:none"></div>
                </form>

                ${models.length > 0 ? `
                <div style="font-size:.8rem;font-weight:700;color:var(--text);margin-bottom:.4rem">Modele încărcate (${models.length})</div>
                <table style="width:100%;font-size:.75rem;border-collapse:collapse">
                    <thead><tr style="border-bottom:1px solid var(--border)">
                        <th style="text-align:left;padding:.3rem">Nume</th>
                        <th style="text-align:left;padding:.3rem">Tip</th>
                        <th style="text-align:left;padding:.3rem">Linie / mașină</th>
                        <th style="text-align:left;padding:.3rem">Status</th>
                        <th style="width:40px"></th>
                    </tr></thead>
                    <tbody>${modelRows}</tbody>
                </table>` : '<p style="font-size:.8rem;color:var(--text-muted)">Niciun model 3D încărcat.</p>'}

                <div style="margin-top:1rem;padding:.8rem;background:rgba(0,200,255,.06);border-radius:6px;font-size:.72rem;color:var(--text-muted)">
                    <strong>Formate acceptate:</strong> .stp, .step, .igs, .iges<br>
                    <strong>Conversie:</strong> STEP/IGES → GLB (Three.js) automată pe server<br>
                    <strong>Tip „Hală":</strong> un singur GLB pentru întreaga hală (prioritar față de liniile separate).<br>
                    <strong>Tip „Linie":</strong> câte un fișier STEP/GLB per linie — se compun împreună în 3D (poziție/rotație din DB dacă aliniezi manual).<br>
                    <strong>Tip „Mașină":</strong> înlocuiește silueta acelei mașini cu modelul real.
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', html);

    const sync3dTypeFields = () => {
        const sel = document.querySelector('#overlay-3d-upload select[name="model_type"]');
        const wLine = document.getElementById('wrap-3d-line-id');
        const wMach = document.getElementById('wrap-3d-machine-id');
        if (!sel || !wLine || !wMach) return;
        const v = (sel as HTMLInputElement).value;
        wLine.style.display = v === 'line' ? '' : 'none';
        wMach.style.display = v === 'machine' ? '' : 'none';
    };
    document.querySelector('#overlay-3d-upload select[name="model_type"]')?.addEventListener('change', sync3dTypeFields);
    sync3dTypeFields();

    document.getElementById('form-3d-upload')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target;
        const btn = document.getElementById('btn-3d-submit');
        const statusEl = document.getElementById('upload-3d-status');
        const fileInput = (form as HTMLElement).querySelector('input[name="file"]');
        if (!(fileInput as HTMLInputElement).files!.length) { toast('Selectează un fișier', 'error'); return; }

        (btn as HTMLInputElement).disabled = true;
        btn!.textContent = 'Se încarcă...';
        statusEl!.style.display = '';
        statusEl!.innerHTML = '<span style="color:#eab308">⏳</span> Încărcare fișier pe server...';

        const fd = new FormData();
        fd.append('file', (fileInput as HTMLInputElement).files![0]);
        fd.append('name', ((form as HTMLElement).querySelector('input[name="name"]') as HTMLInputElement).value || (fileInput as HTMLInputElement).files![0].name);
        const mt = ((form as HTMLElement).querySelector('select[name="model_type"]') as HTMLInputElement).value;
        fd.append('model_type', mt);
        if (mt === 'line') {
            fd.append('line_id', ((form as HTMLElement).querySelector('select[name="line_id"]') as HTMLInputElement)?.value || '');
            fd.append('machine_id', '');
        } else {
            fd.append('machine_id', ((form as HTMLElement).querySelector('select[name="machine_id"]') as HTMLInputElement)?.value || '');
            fd.append('line_id', '');
        }
        fd.append('tolerance', ((form as HTMLElement).querySelector('input[name="tolerance"]') as HTMLInputElement).value);
        fd.append('angular_tolerance', ((form as HTMLElement).querySelector('input[name="angular_tolerance"]') as HTMLInputElement).value);

        try {
            const res = await fetch('/api/3d-models', { method: 'POST', body: fd });
            let data;
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('json')) {
                data = await res.json();
            } else {
                throw new Error('Serverul a returnat o pagină HTML în loc de JSON. Posibil fișier prea mare sau timeout.');
            }
            if (res.ok && data.status === 'ok') {
                toast('Fișier încărcat! Conversia rulează în fundal...', 'success');
                statusEl!.innerHTML = '<span style="color:#22c55e">✓</span> Fișier încărcat cu succes! Conversia STEP → GLB rulează pe server...<br><span style="color:#eab308">⏳ Se monitorizează statusul conversiei...</span>';
                btn!.textContent = 'Încărcat! Conversie în curs...';
                _pollConversionStatus(data.id, statusEl, btn);
            } else {
                toast(data.error || 'Eroare la upload', 'error');
                statusEl!.innerHTML = '<span style="color:#ef4444">✕</span> Eroare: ' + escHtml(data.error || 'necunoscută');
                (btn as HTMLInputElement).disabled = false;
                btn!.textContent = 'Încarcă și convertește → GLB';
            }
        } catch(err) {
            toast('Eroare la upload: ' + (err as Error).message, 'error');
            statusEl!.innerHTML = '<span style="color:#ef4444">✕</span> Eroare: ' + escHtml((err as Error).message);
            (btn as HTMLInputElement).disabled = false;
            btn!.textContent = 'Încarcă și convertește → GLB';
        }
    });
}

export async function _pollConversionStatus(modelId: string, statusEl: HTMLElement|null, btn: HTMLElement|null) {
    let elapsed = 0;
    const interval = 5000;
    const maxWait = 600000;
    const poll = async () => {
        if (elapsed > maxWait) {
            statusEl!.innerHTML = '<span style="color:#ef4444">⏱</span> Timeout — conversia durează prea mult. Verifică din nou mai târziu.';
            (btn as HTMLInputElement).disabled = false;
            btn!.textContent = 'Încarcă și convertește → GLB';
            return;
        }
        try {
            const res = await fetch('/api/3d-models/' + modelId + '/status');
            const data = await res.json();
            if (data.conversion_status === 'done') {
                statusEl!.innerHTML = '<span style="color:#22c55e">✓ Conversie finalizată!</span> GLB: ' + (data.file_size_mb || '?') + ' MB';
                toast('Model 3D convertit cu succes!', 'success');
                btn!.textContent = '✓ Finalizat';
                setTimeout(() => {
                    document.getElementById('overlay-3d-upload')?.remove();
                    _show3DUploadDialog();
                }, 1500);
                return;
            } else if (data.conversion_status === 'error') {
                statusEl!.innerHTML = '<span style="color:#ef4444">✕ Eroare conversie:</span> ' + escHtml(data.conversion_error || 'necunoscută');
                (btn as HTMLInputElement).disabled = false;
                btn!.textContent = 'Încarcă și convertește → GLB';
                return;
            } else {
                const mins = Math.floor(elapsed / 60000);
                const secs = Math.floor((elapsed % 60000) / 1000);
                statusEl!.innerHTML = '<span style="color:#22c55e">✓</span> Fișier încărcat. <span style="color:#eab308">⏳ Conversie în curs... (' + mins + ':' + String(secs).padStart(2,'0') + ')</span>';
                elapsed += interval;
                setTimeout(poll, interval);
            }
        } catch(e) {
            elapsed += interval;
            setTimeout(poll, interval);
        }
    };
    setTimeout(poll, 3000);
}

export async function _delete3DModel(modelId: string) {
    if (!await customConfirm('Ștergi acest model 3D?', 'Confirmare ștergere')) return;
    try {
        const res = await fetch('/api/3d-models/' + modelId, { method: 'DELETE' });
        if (res.ok) {
            toast('Model șters', 'success');
            document.getElementById('overlay-3d-upload')?.remove();
            _show3DUploadDialog();
        } else {
            toast('Eroare la ștergere', 'error');
        }
    } catch(e) { toast('Eroare: ' + (e as Error).message, 'error'); }
}


/* ═══ Lucrări la eveniment (P6, 8 aug 2026) ═══════════════════════
 * 146 de intervale active n-au nici ore, nici calendar: nu apar în lista de
 * azi, nu apar la restanțe, nu intră în compliance. Existau doar în tabelul de
 * intervale, deci practic invizibile până își amintea cineva de ele.
 * Fila asta le arată grupate pe evenimentul care le declanșează. Grupul
 * „Schimbare matriță" e marcat AUTOMAT: e singurul eveniment pe care aplicația
 * îl detectează deja (mold_change_log), deci singurul care se poate transforma
 * singur în lucrări de făcut, fără să inventăm un semnal.
 */
let _evMasiniPopulate = false;

export async function loadEventIntervals(): Promise<void> {
    const cont = $('#ev-list');
    if (!cont) return;
    const sel = $('#ev-masina') as HTMLSelectElement | null;

    if (!_evMasiniPopulate && sel) {
        _evMasiniPopulate = true;
        try {
            await _ensureMachineCache();
            (_allMachinesCache || []).forEach((m) => {
                const o = document.createElement('option');
                o.value = m.id; o.textContent = m.name || m.id;
                sel.appendChild(o);
            });
            const evLine = _insertLineSelectBefore(sel, 'ev-line', 'Toate liniile', 'Toate');
            evLine?.addEventListener('change', () => loadEventIntervals());
            sel.addEventListener('change', () => loadEventIntervals());
        } catch { /* lista de mașini e opțională */ }
    }

    cont.innerHTML = '<p class="rpt-hint">Se încarcă…</p>';
    try {
        const evLineVal = (document.getElementById('ev-line') as HTMLSelectElement | null)?.value || '';
        const q = sel && sel.value
            ? `?machine_id=${encodeURIComponent(sel.value)}`
            : (evLineVal ? `?line_id=${encodeURIComponent(evLineVal)}` : '');
        const d: { total: number; grupe: GrupaEveniment[] } = await api('/api/intervals/event-triggered' + q);
        const grupe = d.grupe || [];
        const cnt = $('#ev-count');
        if (cnt) cnt.textContent = `${d.total} lucrări, ${grupe.length} tipuri de eveniment`;
        if (!grupe.length) { cont.innerHTML = '<p class="rpt-hint">Nicio lucrare la eveniment.</p>'; return; }

        cont.innerHTML = grupe.map((g) => {
            const auto = g.auto
                ? '<span class="ev-auto" title="Aplicația detectează singură acest eveniment și deschide lucrările">⚡ automat</span>'
                : '<span class="ev-manual" title="Nu există semnal automat — lucrările se deschid de om">manual</span>';
            const randuri = g.items.map((it) => `
                <tr>
                    <td>${escHtml(it.machine_id || '')}</td>
                    <td>${escHtml(it.interval_text || '')}</td>
                    <td>${escHtml(truncate(it.action_ro || '', 150))}</td>
                    <td>${it.critical ? '<span class="badge-crit">critic</span>' : ''}</td>
                </tr>`).join('');
            return `<details class="ev-grup" ${g.auto ? 'open' : ''}>
                <summary><strong>${escHtml(g.eticheta)}</strong>
                    <span class="rpt-count">${g.items.length}</span> ${auto}</summary>
                <div class="table-scroll">
                  <table class="data-table">
                    <thead><tr><th>Utilaj</th><th>Când</th><th>Lucrarea</th><th></th></tr></thead>
                    <tbody>${randuri}</tbody>
                  </table>
                </div>
            </details>`;
        }).join('');
    } catch (e) {
        cont.innerHTML = `<p style="color:var(--red)">Eroare: ${escHtml((e as Error)?.message || String(e))}</p>`;
    }
}
